"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import dynamic from "next/dynamic";
// URL sync uses window.history.pushState/replaceState so the spatial
// transitions never trigger a Next.js route-level remount. The (home)/layout.tsx
// route group keeps HomeShell mounted for /, /works, /contact, /about,
// /resume, /blog, and /:slug.
import { CustomCursor } from "@/components/ui/Cursor";
import { ContactSystem } from "@/components/ui/ContactSystem";
import { ManifestoSystem } from "@/components/ui/ManifestoSystem";
import { ManifestoOverlay } from "@/components/ui/ManifestoOverlay";
import { ResumeOverlay } from "@/components/ui/ResumeOverlay";
import { BlogOverlay } from "@/components/ui/BlogOverlay";
import { OrbitalOverview, OverviewTrigger } from "@/components/ui/OrbitalOverview";
import { CommandPalette } from "@/components/ui/CommandPalette";
import { SystemView, type TransitionState } from "@/components/ui/SystemView";
import { type DomainId, getAdjacentDomains, DOMAIN_LABELS } from "@/data/domains";
import { DOMAIN_TO_PATH, parsePath } from "@/data/routes";
import { BLOG_POSTS } from "@/data/blog";
import { ORBS_DATA, type OrbData } from "@/data/orbs";

// Split orbs: V-orb (profile) is fixed at center, project orbs orbit around it.
// A project orb only renders if its project is actually available in this
// build (publicProjects excludes prodReady:false protected projects in prod) —
// otherwise a not-yet-ready study would ship a dead orb.
const PROJECT_ORBS: OrbData[] = ORBS_DATA.filter(
  o => o.type === "project" && publicProjects.some(p => p.id === o.slug)
);
const PROFILE_ORB: OrbData = ORBS_DATA.find(o => o.type === "profile")!;
import { OrbCard } from "@/components/ui/OrbCard";
import { OrbitFocusLayer } from "@/components/ui/OrbitFocusLayer";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { ContactCard } from "@/components/ui/ContactCard";
import { ProjectCard } from "@/components/ui/ProjectCard";
import { projects, publicProjects } from "@/data/projects";
import { getScrimColor } from "@/utils/colors";
import { useMotionMode } from "@/context/MotionContext";
import { Footer } from "@/components/sections/Footer";
import { AmbientGradients } from "@/components/canvas/AmbientGradients";
import { colorThemes, type ThemeColors } from "@/components/ui/ThemePicker";
import { StyleGuide } from "@/components/ui/StyleGuide";
import { Button, ChevronLeftIcon, ChevronRightIcon, CloseIcon, ICON_SIZE_SM } from "@/components/ui/Button";
import { Magnetic } from "@/components/ui/Magnetic";
// Projects now live on /work page
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import { useCanAnimate } from "@/hooks/useCanAnimate";
import { useThemeMode } from "@/context/ThemeContext";
import {
  hexToHue,
  hexToRgbString,
  adjustColorLightness,
  THEME_ADJUSTMENT,
  THEME_LIGHTNESS,
} from "@/utils/colors";
import { useLoaderState } from "@/context/LoaderContext";
import { useBootSequence } from "@/hooks/useBootSequence";
import { useZoneState, type ZoneState } from "@/hooks/useZoneState";
import { StarLoader, CRTBootText } from "@/components/ui/StarLoader";
import type { RingGeometryData } from "@/components/canvas/BootStarfield";

// Dynamic-import BootStarfield: heavy R3F/Three.js bundle (~hundreds of KB).
// Splits it into its own chunk so /work, /about, /resume, /blog, /contact
// (which all share HomeShell via the (home) route group) don't pay the parse
// cost up front. ssr:false because BootStarfield needs WebGL.
const BootStarfield = dynamic(
  () => import("@/components/canvas/BootStarfield").then((m) => ({ default: m.BootStarfield })),
  { ssr: false }
);
import { BootIdentityLayer } from "@/components/ui/BootIdentityLayer";
import { CRTOverlay } from "@/components/ui/CRTOverlay";
import { useMetaRing } from "@/hooks/useMetaRing";
import { CornerNav } from "@/components/ui/CornerNav";
import { useProjectData } from "@/hooks/useProjectData";
import { CaseStudyInline } from "@/components/ui/CaseStudyInline";
import { PasswordModal, type ProtectedContent } from "@/components/ui/PasswordModal";
import { ToggleTray } from "@/components/ui/ToggleTray";
import * as spatialAudio from "@/lib/audio/spatialAudio";
import { useAudio } from "@/context/AudioContext";

// Dev mode - enables devtools and protected projects in development
const IS_DEV = process.env.NODE_ENV === "development";

// Theme color definitions
const NONE_THEME: ThemeColors = { base: "#FFFFFF", bright: "#FFFFFF", hue: 0 };
const GOLD_THEME: ThemeColors = { base: "#D4AF37", bright: "#FFD700", hue: 48 };
const PINK_DEBUG_THEME: ThemeColors = { base: "#FF69B4", bright: "#FFB6C1", hue: 330 };

// Static fallback for reduced motion / T0 GPU - redirect to /works
function StaticHomeFallback() {
  useEffect(() => {
    window.location.href = DOMAIN_TO_PATH.projects;
  }, []);

  // Show brief loading state while redirecting
  return (
    <div className="relative min-h-screen bg-[var(--surface-bg)] flex items-center justify-center">
      <p className="text-[rgba(var(--foreground-rgb),0.5)]">Loading...</p>
    </div>
  );
}

// Input mode for phase navigation state management
export type InputMode = 'navigation' | 'peek' | 'modal';

// NOTE: Scroll-driven SpatialOS code removed - boot sequence is now the main experience
// The boot sequence handles all phases (loader, void, ring) with time-based animation
// Ring interactivity (navigation, peek) is handled by BootStarfield

export default function HomeShell() {
  const { mode: themeMode } = useThemeMode();
  const { gpuTier, backgrounded, reducedMotion, mode: motionMode } = useMotionMode();
  // Reduced motion (OS setting) or motion toggled off: the ring jumps to its
  // target instead of spring-animating, and hold-to-spin is disabled.
  const instantRing = reducedMotion || motionMode === "off";
  const instantRingRef = useRef(instantRing);
  instantRingRef.current = instantRing;
  const canAnimate = useCanAnimate();
  const { hasLoaderBeenShown, registerHeroRef } = useLoaderState();
  const mainRef = useRef<HTMLDivElement>(null);

  // ==========================================================================
  // BOOT SEQUENCE (new time-based cinematic)
  // ==========================================================================
  const boot = useBootSequence();

  // Detect deep link on mount (before boot completes)
  const initialDeepLinkRef = useRef<{ slug: string; index: number } | null>(null);
  // Deep-link fade: rAF handle so we can cancel on unmount/backgrounded; hasStarted
  // guard so the effect can safely include only stable deps without racing on rerender.
  const deepLinkFadeRafRef = useRef<number | null>(null);
  const deepLinkFadeStartedRef = useRef(false);
  const deepLinkHandledRef = useRef(false);
  const [deepLinkFadeProgress, setDeepLinkFadeProgress] = useState(0); // 0-1 for cross-fade

  // Check for deep link URL on mount (only project slug URLs abbreviate boot;
  // domain URLs like /contact are applied after boot via the effect below).
  useEffect(() => {
    const parsed = parsePath(window.location.pathname);
    if (parsed?.projectSlug) {
      const targetIndex = PROJECT_ORBS.findIndex(o => o.slug === parsed.projectSlug);
      if (targetIndex !== -1) {
        initialDeepLinkRef.current = { slug: parsed.projectSlug, index: targetIndex };
      } else {
        // The slug names no orb in THIS build — e.g. a prodReady:false study in
        // prod. Previously this branch did nothing, so the ring booted to index
        // 0 and the page showed a different project while the URL still read
        // /<slug>. That shipped once (2026-09-28, /csc rendering VPR) and passed
        // a 200-status check. Put the URL back so the address and the content
        // can never disagree; see src/data/orbs.test.ts.
        window.history.replaceState({}, "", "/");
      }
    }
  }, []);

  // Cross-fade animation for deep links: start during late void phase.
  // Effect may re-run as boot.phaseProgress ticks — `deepLinkFadeStartedRef` guards
  // against kicking a second rAF chain in that window before the first setState lands.
  // `deepLinkFadeProgress` is deliberately NOT in deps: it was previously, and the
  // resulting re-runs stacked animate() closures (each calling setState, each kicking
  // more re-runs). The ref-based guard makes the effect idempotent.
  useEffect(() => {
    if (!initialDeepLinkRef.current) return;
    if (boot.phase !== "void") return;
    if (deepLinkFadeStartedRef.current) return;
    if (backgrounded) return; // Don't start the fade if the tab is hidden

    // Start cross-fade when void is ~70% complete (around 1.2s into 1.8s void phase)
    if (boot.phaseProgress >= 0.7) {
      deepLinkFadeStartedRef.current = true;

      // Animate fade progress from 0 to 1 over 600ms
      const startTime = performance.now();
      const duration = 600;

      const animate = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(1, elapsed / duration);
        const eased = 1 - Math.pow(1 - progress, 3); // Ease out cubic
        setDeepLinkFadeProgress(eased);

        if (progress < 1) {
          deepLinkFadeRafRef.current = requestAnimationFrame(animate);
        } else {
          deepLinkFadeRafRef.current = null;
        }
      };

      deepLinkFadeRafRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (deepLinkFadeRafRef.current !== null) {
        cancelAnimationFrame(deepLinkFadeRafRef.current);
        deepLinkFadeRafRef.current = null;
      }
    };
  }, [boot.phase, boot.phaseProgress, backgrounded]);

  // Simulate assets loading after 300ms (will be replaced with real asset loading)
  useEffect(() => {
    const timer = setTimeout(() => {
      boot.onAssetsLoaded();
    }, 300);
    return () => clearTimeout(timer);
  }, [boot.onAssetsLoaded]);

  // Lock scroll during boot sequence (activeDomain scroll lock handled below after state declaration)
  useEffect(() => {
    if (!boot.isComplete) {
      // Lock scroll during boot phases
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      // Stop Lenis smooth scroll (if initialized)
      lenisRef.current?.stop();
    }
    // Note: unlock handled by the second scroll effect that runs after activeDomain is declared
  }, [boot.isComplete]);

  // ==========================================================================
  // VIEWPORT SIZE TRACKING
  // ==========================================================================
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 800
  );
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ==========================================================================
  // META RING NAVIGATION SYSTEM STATE (declared early for useZoneState)
  // ==========================================================================
  // Four domains: projects, contact, blog, manifesto - arranged in circular order
  const [activeDomain, setActiveDomain] = useState<DomainId>("projects");

  // ==========================================================================
  // ZONE STATE MACHINE (idle/peek/read)
  // ==========================================================================
  // Z1/Z3 are content-locked, Z2 (ring) is flex remainder
  const zone = useZoneState({ viewportHeight, viewportWidth, initialOrb: 0, activeDomain });

  // ==========================================================================
  // RING INTERACTION STATE
  // ==========================================================================
  // Ring uses angle-based rotation. activeIndex is derived from ringAngle.
  // Start at 0 so VPR (index 0) is at front/focused after stagger reveal
  const [ringAngle, setRingAngle] = useState(0); // Current ring rotation (radians)
  // isPeeking now derived from zone state (zone.isPeek || zone.isRead)
  const isPeeking = zone.isPeek || zone.isRead; // Legacy compat - TODO: remove after full migration
  const [isRingSpinning, setIsRingSpinning] = useState(false); // Ring has momentum (for CRT scanline flicker)
  const [crtEnabled, setCrtEnabled] = useState(true); // CRT effects (scanlines, trapezoid text)
  const [ringGeometry, setRingGeometry] = useState<RingGeometryData | null>(null); // Ring geometry for card positioning
  const [stripHoveredIndex, setStripHoveredIndex] = useState<number | null>(null); // Strip orb hover for tooltip
  const [peekProgress, setPeekProgress] = useState(0); // 0-1 animation progress for peek state
  // Scroll-driven morph progress: 0 = peek (ring visible), 1 = read (orbs in nav strip)
  const [scrollProgress, setScrollProgress] = useState(0);
  const scrollAccumulatorRef = useRef(0); // Accumulates wheel delta
  const scrollAnimationRef = useRef<number | null>(null); // RAF handle for keyboard-triggered scroll animation
  // Read mode scroll position tracking (for bottom gradient fade)
  const [readIsAtBottom, setReadIsAtBottom] = useState(false);
  const readScrollContainerRef = useRef<HTMLDivElement>(null);
  // Flag to track if we've handled the initial URL (prevents re-running on navigateToOrb changes)
  const hasHandledInitialUrlRef = useRef(false);
  // Tab visibility is now sourced from useMotionMode().backgrounded (via usePageVisibility).

  // ==========================================================================
  // PROTECTED CONTENT STATE
  // ==========================================================================
  // Tracks which protected projects have been unlocked in this session
  const [unlockedProjects, setUnlockedProjects] = useState<Set<string>>(new Set());
  // Protected content cache after unlock (keyed by project ID)
  const [protectedContentMap, setProtectedContentMap] = useState<Map<string, ProtectedContent>>(new Map());
  // Currently prompting for password (null = no prompt)
  const [passwordPromptProject, setPasswordPromptProject] = useState<import("@/data/projects").Project | null>(null);

  // Spring physics constants
  const ORB_SPACING = (Math.PI * 2) / PROJECT_ORBS.length; // Radians between orbs (6 project orbs)
  const SPRING_STIFFNESS = 0.12; // Spring constant (higher = snappier)
  const SPRING_DAMPING = 0.72; // Damping (lower = more bounce)
  const SPRING_SETTLE_THRESHOLD = 0.0001; // When spring is "settled"
  const DRAG_SENSITIVITY = 0.008; // Radians per pixel during drag

  // Spring animation refs (project ring)
  const springVelRef = useRef(0); // Spring velocity
  const snapTargetRef = useRef<number | null>(null); // Target angle for spring (null = no spring)
  const springRafRef = useRef<number | null>(null); // RAF handle
  const isDraggingRef = useRef(false);
  const dragStartAngleRef = useRef(0);

  // ==========================================================================
  // CONTACT RING STATE
  // ==========================================================================
  const [contactRingAngle, setContactRingAngle] = useState(0);
  const CONTACT_ORB_SPACING = (Math.PI * 2) / 3; // 3 moons at 120° intervals

  // Spring animation refs (contact ring)
  const contactSpringVelRef = useRef(0);
  const contactSnapTargetRef = useRef<number | null>(null);
  const contactSpringRafRef = useRef<number | null>(null);
  const isContactDraggingRef = useRef(false);
  const contactDragStartAngleRef = useRef(0);

  // Keyboard spin refs (for continuous spinning while arrow key held)
  const keySpinDirectionRef = useRef<-1 | 1 | null>(null); // -1 = left, 1 = right, null = not spinning
  const keySpinRafRef = useRef<number | null>(null);
  const keySpinStartTimeRef = useRef<number>(0); // When continuous spin started (for easing)
  const keyHoldTimeoutRef = useRef<NodeJS.Timeout | null>(null); // 500ms delay before continuous spin
  const keyTapHandledRef = useRef<boolean>(false); // Track if tap already navigated
  const KEY_HOLD_THRESHOLD_MS = 500; // Hold this long to start continuous spin
  const KEY_SPIN_MIN = 0.14; // Initial speed (fast start)
  const KEY_SPIN_MAX = 0.22; // Max radians per frame
  const KEY_SPIN_RAMP_MS = 250; // Quick ramp to max

  // Contact ring keyboard spin refs (same behavior as project ring)
  const contactKeySpinDirectionRef = useRef<-1 | 1 | null>(null);
  const contactKeySpinRafRef = useRef<number | null>(null);
  const contactKeySpinStartTimeRef = useRef<number>(0);
  const contactKeyHoldTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const contactKeyTapHandledRef = useRef<boolean>(false);

  // Scroll constants
  const SCROLL_DISTANCE = 400; // Pixels to go from peek (0%) to read (100%)
  const KEY_SCROLL_SPEED = 8; // Pixels per frame when holding arrow key in read mode

  // Read mode scroll refs (for continuous scrolling while arrow key held)
  const readScrollDirectionRef = useRef<-1 | 1 | null>(null); // -1 = up, 1 = down, null = not scrolling
  const readScrollRafRef = useRef<number | null>(null);

  // Derive activeIndex from ringAngle (which orb is closest to front)
  // Front position = angle 0, so orb i is at angle -i * ORB_SPACING
  const activeIndex = useMemo(() => {
    const normalizedAngle = (((-ringAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2));
    const index = Math.round(normalizedAngle / ORB_SPACING) % PROJECT_ORBS.length;
    return index;
  }, [ringAngle, ORB_SPACING]);

  // Get active orb slug and project data
  const activeOrbSlug = activeIndex >= 0 ? PROJECT_ORBS[activeIndex]?.slug : undefined;
  const { project: activeProject, isProtected: activeProjectIsProtected } = useProjectData(activeOrbSlug);

  // ==========================================================================
  // META RING NAVIGATION SYSTEM STATE (continued)
  // ==========================================================================
  // activeDomain is declared earlier (before useZoneState) since the zone hook needs it
  const [previousDomain, setPreviousDomain] = useState<DomainId>("projects");
  const [transitionState, setTransitionState] = useState<TransitionState>("idle");
  const [targetDomain, setTargetDomain] = useState<DomainId | null>(null);

  // Meta ring hook - provides adjacent domains and spin state for corner nav
  const metaRing = useMetaRing("projects");

  // Orbital Overview + Command Palette state
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Blog deep scroll — true when user has scrolled past the orb spacer into the card grid
  const [blogInitialPostId, setBlogInitialPostId] = useState<string | null>(null);
  const [blogDeepScroll, setBlogDeepScroll] = useState(false);
  // Blog any-scroll — fires on scrollTop > ~20, used to dim the hero early so
  // the sticky pill bar stops colliding with the name. Separate from deep
  // scroll because deep scroll gates orb pause + audio, which shouldn't fire
  // on a twitch of the scroll wheel.
  const [blogScrolled, setBlogScrolled] = useState(false);
  const [blogLightboxOpen, setBlogLightboxOpen] = useState(false);
  // Stable callback — must not be inline or it recreates every render, causing
  // BlogOverlay's reportDeepScroll to change, re-running the visibility effect,
  // and cancelling the showContent timer before it fires (feed never appears).
  const handleBlogDeepScroll = useCallback((isDeep: boolean) => {
    setBlogDeepScroll(isDeep);
    if (isDeep) spatialAudio.playBlogEnterRead();
  }, []);
  const handleBlogScrolled = useCallback((scrolled: boolean) => {
    setBlogScrolled(scrolled);
  }, []);
  const handleBlogLightboxChange = useCallback((isOpen: boolean) => {
    setBlogLightboxOpen(isOpen);
  }, []);

  // Scroll states for content domains — pause when scrolled, resume at bottom
  const [manifestoScrolled, setManifestoScrolled] = useState(false);
  const [manifestoAtBottom, setManifestoAtBottom] = useState(false);
  const [resumeScrolled, setResumeScrolled] = useState(false);
  const [resumeAtBottom, setResumeAtBottom] = useState(false);
  const handleManifestoScrolled = useCallback((scrolled: boolean) => {
    setManifestoScrolled(scrolled);
  }, []);
  const handleManifestoAtBottom = useCallback((atBottom: boolean) => {
    setManifestoAtBottom(atBottom);
  }, []);
  const handleResumeScrolled = useCallback((scrolled: boolean) => {
    setResumeScrolled(scrolled);
  }, []);
  const handleResumeAtBottom = useCallback((atBottom: boolean) => {
    setResumeAtBottom(atBottom);
  }, []);

  // Helper to delay for transitions
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Navigate between domains with Kirby Crystal Shards zoom effect
  const navigateToDomain = useCallback(async (target: DomainId) => {
    if (target === activeDomain || transitionState !== "idle") return;

    setTargetDomain(target);

    // Reset peek state when leaving projects
    if (activeDomain === "projects") {
      zone.goToIdle();
      setPeekProgress(0);
    }

    // Trigger meta ring spin animation (spring physics)
    metaRing.spinTo(target);

    setPreviousDomain(activeDomain);

    // Phase 1: Zoom out current domain
    setTransitionState("zooming-out");
    spatialAudio.playCollapse();
    await delay(300);

    // Phase 2: Switch active domain
    setActiveDomain(target);
    // Sync URL to the new domain. Skip when the path already matches so we
    // don't stack duplicate history entries on URL-initiated domain landings.
    const nextPath = DOMAIN_TO_PATH[target];
    if (typeof window !== "undefined" && window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath);
    }
    setTransitionState("zooming-in");
    spatialAudio.playExpand();
    await delay(300);

    // Phase 3: Idle - transition complete
    setTransitionState("idle");
    setTargetDomain(null);

    // When returning to projects, force viewport reset
    if (target === "projects") {
      window.scrollTo(0, 0);
      document.body.style.transform = "";
      document.documentElement.style.transform = "";
      window.dispatchEvent(new Event("resize"));
    }
  }, [activeDomain, transitionState, zone.goToIdle, metaRing.spinTo]);

  // Legacy compat - derive isContactOpen/isManifestoOpen from activeDomain
  const isContactOpen = activeDomain === "contact";
  const isManifestoOpen = activeDomain === "manifesto";

  // Calculate active contact moon index from contactRingAngle
  const activeContactIndex = useMemo(() => {
    const normalizedAngle = (((-contactRingAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2));
    return Math.round(normalizedAngle / CONTACT_ORB_SPACING) % 3;
  }, [contactRingAngle, CONTACT_ORB_SPACING]);

  // Compute input mode from system/peek states
  const inputMode: InputMode = activeDomain !== "projects" ? 'modal' : isPeeking ? 'peek' : 'navigation';

  // Ring stays visible for all systems - manifesto text floats over it
  const isRingBackgroundMode = false;

  // Interpolated Z3 height during scroll-to-read morph
  // This coordinates the card rising with orbs traveling to nav strip
  const peekZ3 = Math.round(viewportHeight * 0.55);
  const readZ3 = viewportHeight - 52 - 8; // Z1_HEIGHTS.read = 52
  const morphedZ3Height = zone.isPeek
    ? Math.round(peekZ3 + (readZ3 - peekZ3) * scrollProgress)
    : zone.z3Height;

  // Lock scroll when viewing contact/manifesto systems OR when in peek/read mode
  useEffect(() => {
    if (!boot.isComplete) return; // Boot scroll lock handles this case

    // Lock body scroll for non-projects domains OR when viewing a case study (peek/read)
    const shouldLockScroll = activeDomain !== "projects" || zone.isPeek || zone.isRead;

    if (shouldLockScroll) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      lenisRef.current?.stop();
    } else {
      // Unlock scroll when back to main projects idle
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      // Reset scroll position to prevent canvas clipping from stale scroll state
      window.scrollTo(0, 0);
      lenisRef.current?.scrollTo(0, { immediate: true });
      lenisRef.current?.start();
    }
  }, [activeDomain, boot.isComplete, zone.isPeek, zone.isRead]);

  // Stop Lenis during peek/read mode so wheel events reach our scroll container
  useEffect(() => {
    if (zone.isPeek || zone.isRead) {
      lenisRef.current?.stop();
    } else if (boot.isComplete && activeDomain === "projects") {
      // Only restart Lenis if we're in main system (other effect handles non-main)
      lenisRef.current?.start();
    }
  }, [zone.isPeek, zone.isRead, boot.isComplete, activeDomain]);

  // Listen for CRT toggle from debug overlay
  useEffect(() => {
    const handleCrtToggle = (e: CustomEvent<boolean>) => setCrtEnabled(e.detail);
    window.addEventListener("toggle-crt", handleCrtToggle as EventListener);
    return () => window.removeEventListener("toggle-crt", handleCrtToggle as EventListener);
  }, []);

  // Broadcast CRT state changes to debug overlay
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("crt-state-change", { detail: crtEnabled }));
  }, [crtEnabled]);

  // ==========================================================================
  // SPRING ANIMATION
  // ==========================================================================
  // Runs after drag release or when navigating to a specific orb
  const startSpringAnimation = useCallback(() => {
    // Cancel any existing animation
    if (springRafRef.current) {
      cancelAnimationFrame(springRafRef.current);
    }

    const animate = () => {
      const target = snapTargetRef.current;
      if (target === null) return;

      setRingAngle((current) => {
        // Calculate shortest path to target
        let diff = target - current;
        // Normalize to [-PI, PI] for shortest path
        while (diff > Math.PI) diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;

        // Spring physics
        springVelRef.current += diff * SPRING_STIFFNESS;
        springVelRef.current *= SPRING_DAMPING;

        const newAngle = current + springVelRef.current;

        // Check if settled
        if (Math.abs(springVelRef.current) < SPRING_SETTLE_THRESHOLD && Math.abs(diff) < 0.001) {
          snapTargetRef.current = null;
          springVelRef.current = 0;
          isDraggingRef.current = false;
          return target; // Snap exactly to target
        }

        return newAngle;
      });

      // Continue animation if spring is still active
      if (snapTargetRef.current !== null) {
        springRafRef.current = requestAnimationFrame(animate);
      }
    };

    springRafRef.current = requestAnimationFrame(animate);
  }, [SPRING_STIFFNESS, SPRING_DAMPING, SPRING_SETTLE_THRESHOLD]);

  // Navigate ring to bring orb at targetIndex to front
  const navigateToOrb = useCallback((targetIndex: number) => {
    // Target angle = -targetIndex * ORB_SPACING
    const targetAngle = -targetIndex * ORB_SPACING;

    // Find shortest path from current angle
    let current = ringAngle;
    // Normalize current to reasonable range
    while (current > Math.PI) current -= Math.PI * 2;
    while (current < -Math.PI) current += Math.PI * 2;

    // Calculate which target is closest (accounting for wrap-around)
    let closestTarget = targetAngle;
    const candidates = [targetAngle, targetAngle + Math.PI * 2, targetAngle - Math.PI * 2];
    let minDist = Infinity;
    for (const candidate of candidates) {
      const dist = Math.abs(candidate - current);
      if (dist < minDist) {
        minDist = dist;
        closestTarget = candidate;
      }
    }

    if (instantRing) {
      // No animated rotation under reduced motion: land on the target directly.
      if (springRafRef.current) cancelAnimationFrame(springRafRef.current);
      snapTargetRef.current = null;
      springVelRef.current = 0;
      setRingAngle(closestTarget);
      return;
    }

    snapTargetRef.current = closestTarget;
    startSpringAnimation();
  }, [ringAngle, ORB_SPACING, startSpringAnimation, instantRing]);

  // Ring navigation handlers (arrow keys)
  const handleRotateRing = useCallback((direction: 1 | -1) => {
    // Step to next orb in direction
    const currentIndex = activeIndex;
    const orbCount = PROJECT_ORBS.length;
    const newIndex = ((currentIndex + direction) % orbCount + orbCount) % orbCount;
    spatialAudio.playSnap();
    navigateToOrb(newIndex);
  }, [activeIndex, navigateToOrb]);

  // Split tap rotation handlers (for active orb left/right zones)
  const handleRotateLeft = useCallback(() => handleRotateRing(-1), [handleRotateRing]);
  const handleRotateRight = useCallback(() => handleRotateRing(1), [handleRotateRing]);

  // Track which side of the active orb is being hovered (for OrbCard arrow highlights)
  const [orbHoveredSide, setOrbHoveredSide] = useState<"left" | "right" | null>(null);

  // Track which arrow key is currently pressed (for OrbCard arrow highlights)
  const [arrowKeyPressed, setArrowKeyPressed] = useState<"left" | "right" | null>(null);
  const [contactArrowKeyPressed, setContactArrowKeyPressed] = useState<"left" | "right" | null>(null);

  // Track if lightbox is open (to hide corner nav and lock icon)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Ref to call handleRotateRing without dependency issues in useEffect
  const handleRotateRingRef = useRef(handleRotateRing);
  handleRotateRingRef.current = handleRotateRing;

  // Focus a specific orb (tap to rotate + open peek)
  // When clicking strip orbs (in read mode), also reset scroll progress
  // Note: Uses refs to avoid stale closures
  const handleOrbFocus = useCallback((targetIndex: number) => {
    if (!boot.isComplete) return;

    // Use ref for current read state (avoids stale closure)
    const isCurrentlyInRead = zoneIsReadRef.current;

    // If clicking same orb that's already active
    if (targetIndex === activeIndex) {
      if (isCurrentlyInRead) {
        // In read mode - go back to peek
        zone.goToPeek(targetIndex);
        setScrollProgress(0);
        scrollAccumulatorRef.current = 0;
        window.history.pushState({}, "", DOMAIN_TO_PATH.projects);
      } else if (!zone.isPeek) {
        // In idle mode - enter peek
        zone.goToPeek(targetIndex);
        window.history.pushState({}, "", DOMAIN_TO_PATH.projects);
      }
      // If already in peek, do nothing
      return;
    }

    // Switching to different orb - always go to peek
    navigateToOrb(targetIndex);
    zone.goToPeek(targetIndex);

    // Reset scroll progress so orbs return to ring position
    setScrollProgress(0);
    scrollAccumulatorRef.current = 0;

    // Peek mode always uses root URL (slug only appears in read mode)
    window.history.pushState({}, "", DOMAIN_TO_PATH.projects);
  }, [boot.isComplete, activeIndex, navigateToOrb, zone.goToPeek, zone.isPeek]);

  // Focus orb from background (when viewing C/M) - navigates to Projects + peek
  const handleOrbFocusFromBackground = useCallback((targetIndex: number) => {
    if (!boot.isComplete) return;

    // Navigate to Projects domain first
    navigateToDomain("projects");

    // Set the target orb
    navigateToOrb(targetIndex);

    // V-orb (index -1): just navigate, no peek
    // Project orbs (index >= 0): also enter peek mode after transition
    if (targetIndex >= 0) {
      // Delay peek until after system transition completes
      setTimeout(() => {
        zone.goToPeek(targetIndex);
      }, 650); // After 600ms transition + small buffer
    }
  }, [boot.isComplete, navigateToDomain, navigateToOrb, zone.goToPeek]);

  const handleClosePeek = useCallback(() => {
    zone.goToIdle();
    // Reset deep link state if we came from a deep link
    setDeepLinkFadeProgress(0);
    // Update URL back to root when closing peek
    window.history.pushState({}, "", DOMAIN_TO_PATH.projects);
  }, [zone.goToIdle]);

  // Navigate to a different project (from next/prev buttons in CaseStudyInline)
  const handleNavigateToProject = useCallback((slug: string) => {
    // Find the orb index for this slug
    const targetIndex = PROJECT_ORBS.findIndex(o => o.slug === slug);
    if (targetIndex === -1) return;

    // Find the target project to check if protected
    const targetProject = publicProjects.find(p => p.id === slug);
    if (targetProject?.isProtected && !unlockedProjects.has(slug)) {
      // Target project is protected - show password modal in peek mode
      navigateToOrb(targetIndex);
      setPasswordPromptProject(targetProject);
      // Go back to peek mode while password modal is shown - clear URL
      zone.goToPeek(targetIndex);
      setScrollProgress(0);
      scrollAccumulatorRef.current = 0;
      window.history.pushState({}, "", DOMAIN_TO_PATH.projects);
      return;
    }

    // Navigate to that orb and stay in read mode
    navigateToOrb(targetIndex);

    // Scroll case study content to top
    if (readScrollContainerRef.current) {
      readScrollContainerRef.current.scrollTop = 0;
    }

    // Update URL to the new project
    window.history.pushState({ slug }, "", `/${slug}`);
  }, [navigateToOrb, unlockedProjects, zone.goToPeek]);

  // Handle password unlock success - animate into read mode
  const handlePasswordSuccess = useCallback((projectId: string, content: ProtectedContent) => {
    setUnlockedProjects(prev => new Set([...prev, projectId]));
    setProtectedContentMap(prev => new Map(prev).set(projectId, content));
    setPasswordPromptProject(null);

    // Animate scroll progress from 0 to 1 for smooth transition
    const ANIMATION_DURATION = 600; // ms
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(1, elapsed / ANIMATION_DURATION);

      // Ease out cubic for smooth deceleration
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      setScrollProgress(easedProgress);
      scrollAccumulatorRef.current = SCROLL_DISTANCE * easedProgress;

      // Trigger read mode at threshold
      if (easedProgress >= 0.8 && !zoneIsReadRef.current) {
        zoneIsReadRef.current = true;
        zone.goToRead();
        // Update URL
        window.history.pushState({ slug: projectId }, "", `/${projectId}`);
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [zone.goToRead]);

  // Handle password modal close
  const handlePasswordClose = useCallback(() => {
    setPasswordPromptProject(null);
  }, []);

  // Animate scrollProgress smoothly to target (for keyboard navigation)
  const animateScrollProgress = useCallback((target: number, duration: number = 300) => {
    // Cancel any existing animation
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
    }

    const startValue = scrollProgress;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(1, elapsed / duration);

      // Ease-out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const newValue = startValue + (target - startValue) * eased;

      setScrollProgress(newValue);
      scrollAccumulatorRef.current = newValue * SCROLL_DISTANCE;

      if (progress < 1) {
        scrollAnimationRef.current = requestAnimationFrame(animate);
      } else {
        scrollAnimationRef.current = null;
      }
    };

    scrollAnimationRef.current = requestAnimationFrame(animate);
  }, [scrollProgress]);

  // Ref to access animateScrollProgress without stale closures in keyboard effect
  const animateScrollProgressRef = useRef(animateScrollProgress);
  animateScrollProgressRef.current = animateScrollProgress;

  // ==========================================================================
  // DRAG TO ROTATE HANDLERS
  // ==========================================================================
  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true;
    dragStartAngleRef.current = ringAngle;

    // Cancel any ongoing spring animation
    if (springRafRef.current) {
      cancelAnimationFrame(springRafRef.current);
      springRafRef.current = null;
    }
    snapTargetRef.current = null;
    springVelRef.current = 0;
  }, [ringAngle]);

  const handleDragMove = useCallback((deltaX: number) => {
    // Update ring angle directly during drag
    const newAngle = dragStartAngleRef.current + deltaX * DRAG_SENSITIVITY;
    setRingAngle(newAngle);
  }, [DRAG_SENSITIVITY]);

  const handleDragEnd = useCallback(() => {
    isDraggingRef.current = false;

    // Find nearest orb angle and spring to it
    const normalizedAngle = (((-ringAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2));
    const nearestIndex = Math.round(normalizedAngle / ORB_SPACING) % PROJECT_ORBS.length;
    const targetAngle = -nearestIndex * ORB_SPACING;

    // Use current ringAngle to find closest equivalent target
    let closestTarget = targetAngle;
    const candidates = [targetAngle, targetAngle + Math.PI * 2, targetAngle - Math.PI * 2];
    let minDist = Infinity;
    for (const candidate of candidates) {
      const dist = Math.abs(candidate - ringAngle);
      if (dist < minDist) {
        minDist = dist;
        closestTarget = candidate;
      }
    }

    snapTargetRef.current = closestTarget;
    startSpringAnimation();
  }, [ringAngle, ORB_SPACING, startSpringAnimation]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (springRafRef.current) {
        cancelAnimationFrame(springRafRef.current);
      }
      if (contactSpringRafRef.current) {
        cancelAnimationFrame(contactSpringRafRef.current);
      }
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, []);

  // ==========================================================================
  // CONTACT RING DRAG HANDLERS
  // ==========================================================================
  const startContactSpringAnimation = useCallback(() => {
    if (contactSpringRafRef.current) {
      cancelAnimationFrame(contactSpringRafRef.current);
    }

    const animate = () => {
      const target = contactSnapTargetRef.current;
      if (target === null) return;

      setContactRingAngle((current) => {
        const diff = target - current;

        // Apply spring physics
        contactSpringVelRef.current += diff * SPRING_STIFFNESS;
        contactSpringVelRef.current *= SPRING_DAMPING;

        const newAngle = current + contactSpringVelRef.current;

        // Check if settled
        if (Math.abs(contactSpringVelRef.current) < SPRING_SETTLE_THRESHOLD && Math.abs(diff) < 0.001) {
          contactSnapTargetRef.current = null;
          contactSpringVelRef.current = 0;
          isContactDraggingRef.current = false;
          return target; // Snap to exact target
        }

        return newAngle;
      });

      if (contactSnapTargetRef.current !== null) {
        contactSpringRafRef.current = requestAnimationFrame(animate);
      }
    };

    contactSpringRafRef.current = requestAnimationFrame(animate);
  }, [SPRING_STIFFNESS, SPRING_DAMPING, SPRING_SETTLE_THRESHOLD]);

  const handleContactDragStart = useCallback(() => {
    isContactDraggingRef.current = true;
    contactDragStartAngleRef.current = contactRingAngle;

    if (contactSpringRafRef.current) {
      cancelAnimationFrame(contactSpringRafRef.current);
      contactSpringRafRef.current = null;
    }
    contactSnapTargetRef.current = null;
    contactSpringVelRef.current = 0;
  }, [contactRingAngle]);

  const handleContactDragMove = useCallback((deltaX: number) => {
    const newAngle = contactDragStartAngleRef.current + deltaX * DRAG_SENSITIVITY;
    setContactRingAngle(newAngle);
  }, [DRAG_SENSITIVITY]);

  const handleContactDragEnd = useCallback(() => {
    isContactDraggingRef.current = false;

    // Find nearest moon angle and spring to it (3 moons)
    const normalizedAngle = (((-contactRingAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2));
    const nearestIndex = Math.round(normalizedAngle / CONTACT_ORB_SPACING) % 3;
    const targetAngle = -nearestIndex * CONTACT_ORB_SPACING;

    // Find closest equivalent target
    let closestTarget = targetAngle;
    const candidates = [targetAngle, targetAngle + Math.PI * 2, targetAngle - Math.PI * 2];
    let minDist = Infinity;
    for (const candidate of candidates) {
      const dist = Math.abs(candidate - contactRingAngle);
      if (dist < minDist) {
        minDist = dist;
        closestTarget = candidate;
      }
    }

    contactSnapTargetRef.current = closestTarget;
    startContactSpringAnimation();
  }, [contactRingAngle, CONTACT_ORB_SPACING, startContactSpringAnimation]);

  // Rotate contact ring left (to previous moon)
  const handleContactRotateLeft = useCallback(() => {
    // Round current angle to nearest moon position, then add spacing
    // This prevents drift from accumulated floating point errors
    const nearestMoonAngle = Math.round(contactRingAngle / CONTACT_ORB_SPACING) * CONTACT_ORB_SPACING;
    const targetAngle = nearestMoonAngle + CONTACT_ORB_SPACING;
    contactSnapTargetRef.current = targetAngle;
    spatialAudio.playSnap();
    startContactSpringAnimation();
  }, [contactRingAngle, CONTACT_ORB_SPACING, startContactSpringAnimation]);

  // Rotate contact ring right (to next moon)
  const handleContactRotateRight = useCallback(() => {
    // Round current angle to nearest moon position, then subtract spacing
    // This prevents drift from accumulated floating point errors
    const nearestMoonAngle = Math.round(contactRingAngle / CONTACT_ORB_SPACING) * CONTACT_ORB_SPACING;
    const targetAngle = nearestMoonAngle - CONTACT_ORB_SPACING;
    contactSnapTargetRef.current = targetAngle;
    spatialAudio.playSnap();
    startContactSpringAnimation();
  }, [contactRingAngle, CONTACT_ORB_SPACING, startContactSpringAnimation]);

  // Rotate contact ring to bring a specific moon to front position
  const handleContactRotateToMoon = useCallback((moonIndex: number) => {
    // Target angle places the moon at front (angle 0)
    // Each moon is at baseAngle = (i/3) * 2π, so to bring moon i to front: ringAngle = -i * spacing
    const targetAngle = -moonIndex * CONTACT_ORB_SPACING;
    // Find shortest path from current angle (avoid spinning the long way around)
    const diff = targetAngle - contactRingAngle;
    const shortestDiff = ((diff % (Math.PI * 2)) + Math.PI * 3) % (Math.PI * 2) - Math.PI;
    contactSnapTargetRef.current = contactRingAngle + shortestDiff;
    spatialAudio.playSnap();
    startContactSpringAnimation();
  }, [contactRingAngle, CONTACT_ORB_SPACING, startContactSpringAnimation]);

  // Refs to call contact rotate handlers without dependency issues in useEffect
  const handleContactRotateLeftRef = useRef(handleContactRotateLeft);
  handleContactRotateLeftRef.current = handleContactRotateLeft;
  const handleContactRotateRightRef = useRef(handleContactRotateRight);
  handleContactRotateRightRef.current = handleContactRotateRight;

  // Snap to nearest orb (used when releasing arrow keys or drag)
  const snapToNearestOrb = useCallback(() => {
    // Find nearest orb angle and spring to it
    const orbCount = PROJECT_ORBS.length;
    const orbSpacing = (Math.PI * 2) / orbCount;

    setRingAngle((currentAngle) => {
      const normalizedAngle = (((-currentAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2));
      const nearestIndex = Math.round(normalizedAngle / orbSpacing) % orbCount;
      const targetAngle = -nearestIndex * orbSpacing;

      // Find closest equivalent target (handle wraparound)
      let closestTarget = targetAngle;
      const candidates = [targetAngle, targetAngle + Math.PI * 2, targetAngle - Math.PI * 2];
      let minDist = Infinity;
      for (const candidate of candidates) {
        const dist = Math.abs(candidate - currentAngle);
        if (dist < minDist) {
          minDist = dist;
          closestTarget = candidate;
        }
      }

      snapTargetRef.current = closestTarget;
      return currentAngle; // Don't change angle here, spring will handle it
    });

    startSpringAnimation();
  }, [startSpringAnimation]);

  // Snap to nearest contact moon (used when releasing arrow keys)
  const snapToNearestContactMoon = useCallback(() => {
    const moonCount = 3; // email, tec, linkedin

    setContactRingAngle((currentAngle) => {
      const normalizedAngle = (((-currentAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2));
      const nearestIndex = Math.round(normalizedAngle / CONTACT_ORB_SPACING) % moonCount;
      const targetAngle = -nearestIndex * CONTACT_ORB_SPACING;

      // Find closest equivalent target (handle wraparound)
      let closestTarget = targetAngle;
      const candidates = [targetAngle, targetAngle + Math.PI * 2, targetAngle - Math.PI * 2];
      let minDist = Infinity;
      for (const candidate of candidates) {
        const dist = Math.abs(candidate - currentAngle);
        if (dist < minDist) {
          minDist = dist;
          closestTarget = candidate;
        }
      }

      contactSnapTargetRef.current = closestTarget;
      return currentAngle; // Don't change angle here, spring will handle it
    });

    startContactSpringAnimation();
  }, [CONTACT_ORB_SPACING, startContactSpringAnimation]);

  // Ref to call snapToNearestContactMoon without dependency issues in useEffect
  const snapToNearestContactMoonRef = useRef(snapToNearestContactMoon);
  snapToNearestContactMoonRef.current = snapToNearestContactMoon;

  // Animate peekProgress when isPeeking changes (smooth lerp)
  useEffect(() => {
    let rafId: number;
    const animate = () => {
      setPeekProgress(prev => {
        const target = isPeeking ? 1 : 0;
        const diff = target - prev;
        if (Math.abs(diff) < 0.01) return target;
        rafId = requestAnimationFrame(animate);
        return prev + diff * 0.1;
      });
    };
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [isPeeking]);

  // ==========================================================================
  // SCROLL-TO-READ: Wheel/touch events during peek drive morph to read state
  // ==========================================================================
  // Scroll down: Z3 grows, ring morphs to nav strip, at 80% -> lock into read
  // Scroll up: Z3 shrinks, ring reforms, at 0% -> back to idle

  // Store zone methods in refs to avoid dependency array issues
  const zoneGoToReadRef = useRef(zone.goToRead);
  const zoneGoToIdleRef = useRef(zone.goToIdle);
  const zoneIsReadRef = useRef(zone.isRead);
  const wasPeekingRef = useRef(false);
  const activeProjectIsProtectedRef = useRef(activeProjectIsProtected);
  const activeOrbSlugRef = useRef(activeOrbSlug);
  zoneGoToReadRef.current = zone.goToRead;
  zoneGoToIdleRef.current = zone.goToIdle;
  zoneIsReadRef.current = zone.isRead;
  activeProjectIsProtectedRef.current = activeProjectIsProtected;
  activeOrbSlugRef.current = activeOrbSlug;

  useEffect(() => {
    // Reset scroll progress only when going back to IDLE (not when entering READ)
    // When entering read: keep scrollProgress=1 so orbs stay in strip position
    // When going to idle: reset to 0 so orbs return to ring
    const wasEngaged = wasPeekingRef.current; // Was in peek OR read
    const isNowIdle = !zone.isPeek && !zone.isRead;
    if (isNowIdle && wasEngaged) {
      setScrollProgress(0);
      scrollAccumulatorRef.current = 0;
    }
    // Track whether we're in peek OR read (either counts as "engaged")
    wasPeekingRef.current = zone.isPeek || zone.isRead;

    // Only add scroll-to-read listeners during peek mode transition
    // In read mode, let native scroll handle the content
    if (!zone.isPeek || zone.isRead) {
      return;
    }

    const handleWheel = (e: WheelEvent) => {
      // Skip if already in read mode - let CaseStudyInline handle scrolling
      if (zoneIsReadRef.current) {
        return;
      }

      // Prevent page scroll during peek-to-read transition
      e.preventDefault();

      // Accumulate delta (positive = scroll down = grow Z3)
      scrollAccumulatorRef.current += e.deltaY;

      // Clamp accumulator to valid range
      scrollAccumulatorRef.current = Math.max(
        -SCROLL_DISTANCE * 0.1, // Allow slight overscroll up (for dismiss gesture)
        Math.min(SCROLL_DISTANCE, scrollAccumulatorRef.current)
      );

      // Convert to progress (0-1)
      const progress = Math.max(0, Math.min(1, scrollAccumulatorRef.current / SCROLL_DISTANCE));
      setScrollProgress(progress);

      // Lock into read state at 80% threshold
      // But NOT for protected projects that aren't unlocked yet
      const isProtectedAndLocked = activeProjectIsProtectedRef.current &&
        activeOrbSlugRef.current &&
        !unlockedProjectsRef.current.has(activeOrbSlugRef.current);

      if (progress >= 0.8 && !zoneIsReadRef.current && !isProtectedAndLocked) {
        zoneIsReadRef.current = true; // Update ref immediately to prevent duplicate calls
        zoneGoToReadRef.current();
        setScrollProgress(1); // Snap to full
        scrollAccumulatorRef.current = SCROLL_DISTANCE;
      }

      // For protected projects, completely block scroll progress
      if (isProtectedAndLocked) {
        setScrollProgress(0);
        scrollAccumulatorRef.current = 0;
        return;
      }

      // Dismiss back to idle if scrolling up past threshold
      if (scrollAccumulatorRef.current < -SCROLL_DISTANCE * 0.05) {
        zoneGoToIdleRef.current();
        setScrollProgress(0);
        scrollAccumulatorRef.current = 0;
      }
    };

    // Touch support for mobile
    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const deltaY = touchStartY - e.touches[0].clientY; // Inverted for natural scroll
      touchStartY = e.touches[0].clientY;

      // Simulate wheel event
      handleWheel({ deltaY, preventDefault: () => e.preventDefault() } as WheelEvent);
    };

    // Add listeners
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [zone.isPeek, zone.isRead]); // Re-run when peek/read state changes

  // ==========================================================================
  // URL SYNC: Update URL when entering read mode, handle back button
  // ==========================================================================

  // Track previous read state to detect transitions
  const wasInReadModeRef = useRef(false);

  // Update URL when entering/leaving read mode (scrollProgress crosses 0.8 threshold)
  useEffect(() => {
    const isNowInRead = zone.isRead && scrollProgress >= 0.8;
    const wasInRead = wasInReadModeRef.current;

    if (isNowInRead && !wasInRead && activeOrbSlug && activeProject) {
      // Entering read mode - set slug URL for deep linking
      window.history.pushState({ slug: activeOrbSlug }, "", `/${activeOrbSlug}`);
      // Focus the case study container for keyboard accessibility
      readScrollContainerRef.current?.focus();
    } else if (!isNowInRead && wasInRead) {
      // Leaving read mode - clear URL back to root
      window.history.pushState({}, "", DOMAIN_TO_PATH.projects);
    }

    wasInReadModeRef.current = isNowInRead;
  }, [zone.isRead, scrollProgress, activeOrbSlug, activeProject]);

  // Handle browser back/forward. Re-parse the URL rather than trusting state —
  // Next.js route transitions don't carry our pushState payload.
  useEffect(() => {
    const handlePopState = () => {
      const parsed = parsePath(window.location.pathname);
      if (!parsed) return;

      // Blog post deep link in history.
      if (parsed.blogSlug) {
        const post = BLOG_POSTS.find(p => p.type === "post" && !p.draft && p.slug === parsed.blogSlug);
        if (post) setBlogInitialPostId(post.id);
        if (activeDomain !== "blog") navigateToDomain("blog");
        return;
      }

      // Domain change via back/forward.
      if (!parsed.projectSlug && parsed.domain !== activeDomain) {
        navigateToDomain(parsed.domain);
        return;
      }

      if (parsed.projectSlug) {
        // Project deep link in history.
        const targetIndex = PROJECT_ORBS.findIndex(o => o.slug === parsed.projectSlug);
        if (targetIndex !== -1) navigateToOrb(targetIndex);
        return;
      }

      // Projects root (/works or /) — close peek/read.
      zone.goToIdle();
      setScrollProgress(0);
      scrollAccumulatorRef.current = 0;
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [activeDomain, zone.goToIdle, navigateToOrb, navigateToDomain]);

  // Ref to access unlockedProjects without triggering effect re-runs
  const unlockedProjectsRef = useRef(unlockedProjects);
  unlockedProjectsRef.current = unlockedProjects;

  // Peek → Read (expand), unless the project is protected and still locked.
  // Shared by ArrowDown in peek and the peek card's "scroll to read" control.
  const zoneGoToRead = zone.goToRead;
  const enterReadFromPeek = useCallback(() => {
    const isProtectedAndLocked = activeProjectIsProtectedRef.current &&
      activeOrbSlugRef.current &&
      !unlockedProjectsRef.current.has(activeOrbSlugRef.current);
    if (isProtectedAndLocked) return;
    zoneGoToRead();
    animateScrollProgressRef.current(1, 350);
  }, [zoneGoToRead]);
  const enterReadFromPeekRef = useRef(enterReadFromPeek);
  enterReadFromPeekRef.current = enterReadFromPeek;

  // ==========================================================================
  // DEEP LINK ABBREVIATED BOOT - Skip ring emergence for direct case study access
  // ==========================================================================
  // When user visits /avp, /esr etc directly:
  // loader -> void (particle burst w/ cross-fade) -> read mode

  // Set ring angle early (during void phase) so orb is positioned
  useEffect(() => {
    if (!initialDeepLinkRef.current) return;
    if (boot.phase !== "void") return;

    const { index } = initialDeepLinkRef.current;
    const ORB_SPACING_LOCAL = (Math.PI * 2) / PROJECT_ORBS.length;
    setRingAngle(-index * ORB_SPACING_LOCAL);
  }, [boot.phase]);

  // Enter read mode when cross-fade is halfway done
  useEffect(() => {
    if (!initialDeepLinkRef.current) return;
    if (deepLinkHandledRef.current) return;
    if (deepLinkFadeProgress < 0.5) return;

    deepLinkHandledRef.current = true;
    const { slug } = initialDeepLinkRef.current;

    // Check if project is protected
    const targetProject = publicProjects.find(p => p.id === slug);
    if (targetProject?.isProtected && !unlockedProjectsRef.current.has(slug)) {
      // Protected project - let normal boot complete, then show password modal
      return;
    }

    // Skip ring emergence and go to read mode
    boot.skip();
    zone.goToRead();
    setScrollProgress(1);
    scrollAccumulatorRef.current = SCROLL_DISTANCE;
  }, [deepLinkFadeProgress, boot.skip, zone.goToRead]);

  // Handle URL state on page load/refresh (for non-deep-link scenarios)
  // Note: This handles the case where a /[slug] URL was pushed via pushState
  // and the user refreshed the page. Direct navigation to /[slug] goes to the
  // standalone case study page.
  useEffect(() => {
    if (!boot.isComplete) return;

    // Skip if already handled by deep link abbreviation
    if (deepLinkHandledRef.current) return;

    // Only run once on initial mount (prevents re-running when navigateToOrb changes)
    if (hasHandledInitialUrlRef.current) return;
    hasHandledInitialUrlRef.current = true;

    // Parse the landing URL: either a project slug deep link, or a domain route.
    const parsed = parsePath(window.location.pathname);
    if (!parsed) return;

    // Blog post deep link (/blog/[slug]) — open blog domain then the post.
    if (parsed.blogSlug) {
      const post = BLOG_POSTS.find(p => p.type === "post" && !p.draft && p.slug === parsed.blogSlug);
      if (post) setBlogInitialPostId(post.id);
      navigateToDomain("blog");
      return;
    }

    // Domain deep link (/contact, /about, /resume, /blog, /works) — jump past
    // projects idle into the requested domain.
    if (!parsed.projectSlug && parsed.domain !== "projects") {
      navigateToDomain(parsed.domain);
      return;
    }

    if (!parsed.projectSlug) return;

    const slug = parsed.projectSlug;
    const targetIndex = PROJECT_ORBS.findIndex(o => o.slug === slug);
    if (targetIndex === -1) return;

    // Check if project is protected and needs password
    const targetProject = publicProjects.find(p => p.id === slug);
    if (targetProject?.isProtected && !unlockedProjectsRef.current.has(slug)) {
      // Show password modal in peek mode - clear URL since peek uses /works
      navigateToOrb(targetIndex);
      setPasswordPromptProject(targetProject);
      zone.goToPeek(targetIndex);
      window.history.replaceState({}, "", DOMAIN_TO_PATH.projects);
      return;
    }

    // Navigate to that orb and enter read mode
    navigateToOrb(targetIndex);
    zone.goToPeek(targetIndex);
    // Delay entering read mode to allow peek animation to complete
    setTimeout(() => {
      zone.goToRead();
      setScrollProgress(1);
      scrollAccumulatorRef.current = SCROLL_DISTANCE;
    }, 100);
  }, [boot.isComplete, navigateToOrb, navigateToDomain, zone.goToPeek, zone.goToRead]);

  // Keyboard navigation for ring interaction (tap = step, hold 500ms = continuous spin)
  useEffect(() => {
    if (!boot.isComplete) return;

    // Start continuous spin animation (called after hold threshold)
    const startKeySpin = (direction: -1 | 1) => {
      // Cancel any ongoing spring animation
      if (springRafRef.current) {
        cancelAnimationFrame(springRafRef.current);
        springRafRef.current = null;
      }
      snapTargetRef.current = null;
      springVelRef.current = 0;

      keySpinDirectionRef.current = direction;
      keySpinStartTimeRef.current = performance.now();

      const spinLoop = () => {
        if (keySpinDirectionRef.current === null) return;

        // Ease-out ramp: fast start, eases to max speed
        const elapsed = performance.now() - keySpinStartTimeRef.current;
        const t = Math.min(1, elapsed / KEY_SPIN_RAMP_MS);
        // Ease-out quad: quick acceleration that settles
        const eased = 1 - Math.pow(1 - t, 2);
        const speed = KEY_SPIN_MIN + eased * (KEY_SPIN_MAX - KEY_SPIN_MIN);

        setRingAngle((current) => current + keySpinDirectionRef.current! * speed);
        keySpinRafRef.current = requestAnimationFrame(spinLoop);
      };

      // Start the loop
      if (keySpinRafRef.current) cancelAnimationFrame(keySpinRafRef.current);
      keySpinRafRef.current = requestAnimationFrame(spinLoop);
    };

    // Stop spin and snap to nearest orb
    const stopKeySpin = () => {
      keySpinDirectionRef.current = null;
      if (keySpinRafRef.current) {
        cancelAnimationFrame(keySpinRafRef.current);
        keySpinRafRef.current = null;
      }
      // Clear hold timeout
      if (keyHoldTimeoutRef.current) {
        clearTimeout(keyHoldTimeoutRef.current);
        keyHoldTimeoutRef.current = null;
      }
      snapToNearestOrb();
    };

    // Start continuous spin for contact ring (same logic as project ring)
    const startContactKeySpin = (direction: -1 | 1) => {
      // Cancel any ongoing spring animation
      if (contactSpringRafRef.current) {
        cancelAnimationFrame(contactSpringRafRef.current);
        contactSpringRafRef.current = null;
      }
      contactSnapTargetRef.current = null;
      contactSpringVelRef.current = 0;

      contactKeySpinDirectionRef.current = direction;
      contactKeySpinStartTimeRef.current = performance.now();

      const spinLoop = () => {
        if (contactKeySpinDirectionRef.current === null) return;

        // Ease-out ramp: fast start, eases to max speed
        const elapsed = performance.now() - contactKeySpinStartTimeRef.current;
        const t = Math.min(1, elapsed / KEY_SPIN_RAMP_MS);
        const eased = 1 - Math.pow(1 - t, 2);
        const speed = KEY_SPIN_MIN + eased * (KEY_SPIN_MAX - KEY_SPIN_MIN);

        setContactRingAngle((current) => current + contactKeySpinDirectionRef.current! * speed);
        contactKeySpinRafRef.current = requestAnimationFrame(spinLoop);
      };

      if (contactKeySpinRafRef.current) cancelAnimationFrame(contactKeySpinRafRef.current);
      contactKeySpinRafRef.current = requestAnimationFrame(spinLoop);
    };

    // Stop contact spin and snap to nearest moon
    const stopContactKeySpin = () => {
      contactKeySpinDirectionRef.current = null;
      if (contactKeySpinRafRef.current) {
        cancelAnimationFrame(contactKeySpinRafRef.current);
        contactKeySpinRafRef.current = null;
      }
      if (contactKeyHoldTimeoutRef.current) {
        clearTimeout(contactKeyHoldTimeoutRef.current);
        contactKeyHoldTimeoutRef.current = null;
      }
      snapToNearestContactMoonRef.current();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // If any modal/dialog is open (e.g. blog lightbox), let it handle keys itself.
      // Applies to ESC (close) and arrow keys (prev/next). Don't hijack to navigate
      // the ring or rotate orbs behind the dialog.
      // The orbit peek card is a dialog too, but it is driven by THIS handler
      // (←→ rotate under it, ↓ reads, ↑/Esc close), so it doesn't count here.
      const otherDialogOpen = !!document.querySelector('[role="dialog"]:not([data-orbit-peek])');
      if (
        otherDialogOpen &&
        (e.key === "Escape" || e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "ArrowUp" || e.key === "ArrowDown")
      ) {
        return;
      }

      // ESC: navigate back to main system or close peek
      if (e.key === "Escape") {
        // If any modal/dialog is open (e.g. blog expand overlay), let it handle ESC itself.
        // Don't hijack to navigate home.
        if (otherDialogOpen) {
          return;
        }
        if (activeDomain !== "projects") {
          // Return to main from any other system
          navigateToDomain("projects");
          return;
        }
        if (isPeeking) {
          handleClosePeek();
          return;
        }
        return;
      }

      // Contact ring navigation when in contact system (same tap+hold logic as project ring)
      if (activeDomain === "contact") {
        if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && !e.repeat) {
          const direction = e.key === "ArrowLeft" ? -1 : 1;

          // Light up the arrow indicator
          setContactArrowKeyPressed(e.key === "ArrowLeft" ? "left" : "right");

          // Immediately step to next moon (tap behavior)
          if (e.key === "ArrowLeft") {
            handleContactRotateLeftRef.current();
          } else {
            handleContactRotateRightRef.current();
          }
          contactKeyTapHandledRef.current = true;

          // Set up timeout for continuous spin if held
          if (contactKeyHoldTimeoutRef.current) {
            clearTimeout(contactKeyHoldTimeoutRef.current);
          }
          contactKeyHoldTimeoutRef.current = setTimeout(() => {
            // User held long enough - start continuous spin
            startContactKeySpin(-direction as -1 | 1);
            contactKeyHoldTimeoutRef.current = null;
          }, KEY_HOLD_THRESHOLD_MS);
        }
        return;
      }

      // Block ring navigation when not in main system
      if (activeDomain !== "projects") {
        return;
      }

      // In read mode: block left/right (ring nav), but handle up/down for scrolling
      // Use ref to get current value (avoids stale closure)
      if (zoneIsReadRef.current) {
        // Block left/right ring navigation
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
          return;
        }
        // Cmd+Up/Down: instant jump to top/bottom (bypass smooth scroll)
        if ((e.key === "ArrowDown" || e.key === "ArrowUp") && e.metaKey) {
          e.preventDefault();
          if (readScrollContainerRef.current) {
            const target = e.key === "ArrowDown"
              ? readScrollContainerRef.current.scrollHeight
              : 0;
            readScrollContainerRef.current.scrollTop = target;
          }
          return;
        }
        // Handle up/down for continuous scrolling (only on first press, not repeat)
        if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !e.repeat) {
          const direction = e.key === "ArrowDown" ? 1 : -1;
          readScrollDirectionRef.current = direction;

          // Start continuous scroll animation
          const scrollFrame = () => {
            if (readScrollDirectionRef.current === null) return;
            if (readScrollContainerRef.current) {
              readScrollContainerRef.current.scrollTop += readScrollDirectionRef.current * KEY_SCROLL_SPEED;
            }
            readScrollRafRef.current = requestAnimationFrame(scrollFrame);
          };
          readScrollRafRef.current = requestAnimationFrame(scrollFrame);

          e.preventDefault();
          return;
        }
        return;
      }

      // Ring navigation - tap to step, hold 500ms for continuous spin (ignore repeat events)
      if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && !e.repeat) {
        const direction = e.key === "ArrowLeft" ? -1 : 1;

        // Light up the arrow indicator
        setArrowKeyPressed(e.key === "ArrowLeft" ? "left" : "right");

        // Immediately step to next orb (tap behavior)
        handleRotateRingRef.current(direction);
        keyTapHandledRef.current = true;

        // Set up timeout for continuous spin if held
        if (keyHoldTimeoutRef.current) {
          clearTimeout(keyHoldTimeoutRef.current);
        }
        // Reduced motion: a tap still steps one node; holding never free-spins.
        if (instantRingRef.current) return;
        keyHoldTimeoutRef.current = setTimeout(() => {
          // User held long enough - start continuous spin
          // Note: negate direction because handleRotateRing uses index-based navigation
          // (higher index = more negative angle), while startKeySpin adds directly to angle
          startKeySpin(-direction as -1 | 1);
          keyHoldTimeoutRef.current = null;
        }, KEY_HOLD_THRESHOLD_MS);
        return;
      }

      // Enter to peek - only in navigation mode, and only when Enter isn't
      // activating a control (gear, corner nav, card arrows...). Without this,
      // Enter on any button ALSO opened the peek, and the peek dialog now takes
      // focus. The orbit node buttons handle Enter themselves.
      if (
        e.key === "Enter" &&
        (e.target as HTMLElement | null)?.closest?.('button, a[href], input, select, textarea, [role="button"]')
      ) {
        return;
      }
      if (e.key === "Enter" && inputMode === 'navigation' && !isPeeking) {
        zone.goToPeek(activeIndex);
        // Peek mode uses root URL
        window.history.pushState({}, "", DOMAIN_TO_PATH.projects);
        return;
      }

      // Vertical navigation: ArrowDown/ArrowUp to move between idle ↔ peek ↔ read
      // Down = go deeper (idle → peek → read)
      // Up = go back (read → peek → idle)
      if (e.key === "ArrowDown") {
        if (zone.isIdle && inputMode === 'navigation') {
          // Idle → Peek (enter) - peek uses root URL
          zone.goToPeek(activeIndex);
          window.history.pushState({}, "", DOMAIN_TO_PATH.projects);
        } else if (zone.isPeek) {
          // Peek → Read (expand) - but NOT for protected projects
          enterReadFromPeekRef.current();
        }
        // Read → nothing (already deepest)
        return;
      }
      if (e.key === "ArrowUp") {
        // Note: Read mode is blocked at the top of this handler, so we only reach here in peek/idle
        if (zone.isPeek) {
          // Peek → Idle (close) - clear URL
          zone.goToIdle();
          window.history.pushState({}, "", DOMAIN_TO_PATH.projects);
        }
        // Idle → nothing (already shallowest)
        return;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Stop read mode scrolling when up/down released
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (readScrollDirectionRef.current !== null) {
          readScrollDirectionRef.current = null;
          if (readScrollRafRef.current) {
            cancelAnimationFrame(readScrollRafRef.current);
            readScrollRafRef.current = null;
          }
        }
      }

      // Stop spinning when arrow key is released
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        // Clear arrow indicators
        setArrowKeyPressed(null);
        setContactArrowKeyPressed(null);
        // Clear hold timeout (user released before 500ms = just a tap)
        if (keyHoldTimeoutRef.current) {
          clearTimeout(keyHoldTimeoutRef.current);
          keyHoldTimeoutRef.current = null;
        }
        if (contactKeyHoldTimeoutRef.current) {
          clearTimeout(contactKeyHoldTimeoutRef.current);
          contactKeyHoldTimeoutRef.current = null;
        }
        // Stop continuous spin if running (project ring)
        if (keySpinDirectionRef.current !== null) {
          stopKeySpin();
        }
        // Stop continuous spin if running (contact ring)
        if (contactKeySpinDirectionRef.current !== null) {
          stopContactKeySpin();
        }
        keyTapHandledRef.current = false;
        contactKeyTapHandledRef.current = false;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      // Cleanup any ongoing spin or timeout (project ring)
      if (keySpinRafRef.current) {
        cancelAnimationFrame(keySpinRafRef.current);
      }
      if (keyHoldTimeoutRef.current) {
        clearTimeout(keyHoldTimeoutRef.current);
      }
      // Cleanup any ongoing spin or timeout (contact ring)
      if (contactKeySpinRafRef.current) {
        cancelAnimationFrame(contactKeySpinRafRef.current);
      }
      if (contactKeyHoldTimeoutRef.current) {
        clearTimeout(contactKeyHoldTimeoutRef.current);
      }
      if (readScrollRafRef.current) {
        cancelAnimationFrame(readScrollRafRef.current);
      }
    };
  }, [boot.isComplete, isPeeking, activeDomain, handleClosePeek, snapToNearestOrb, navigateToDomain, KEY_HOLD_THRESHOLD_MS, KEY_SPIN_MIN, KEY_SPIN_MAX, KEY_SPIN_RAMP_MS]);

  // ==========================================================================
  // ORBITAL OVERVIEW (`/`) + COMMAND PALETTE (`Cmd+K`) KEYBOARD TRIGGERS
  // ==========================================================================
  // Separate from main handleKeyDown so they work even when a dialog is open
  // (the overview/palette ARE dialogs, so the main handler would bail).
  useEffect(() => {
    if (!boot.isComplete) return;
    const handler = (e: KeyboardEvent) => {
      // `/` → toggle Orbital Overview (skip if typing in an input)
      if (e.key === "/") {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        e.preventDefault();
        setOverviewOpen((prev) => !prev);
        setPaletteOpen(false);
        return;
      }
      // `Cmd+K` / `Ctrl+K` → toggle Command Palette
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
        setOverviewOpen(false);
        return;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [boot.isComplete]);

  // Track initial loader state at mount (not reactive)
  const loaderWasShownAtMount = useRef(hasLoaderBeenShown);

  // Callback ref that registers immediately when element mounts
  // IMPORTANT: No reactive state in deps - this should only run on mount/unmount
  const heroRefCallback = useCallback((node: HTMLDivElement | null) => {
    mainRef.current = node;
    if (node) {
      registerHeroRef(mainRef);

      // Set initial styles directly on DOM (GSAP will control animation)
      if (loaderWasShownAtMount.current) {
        // Client nav - animate in with GSAP (same as PageEnter)
        node.style.opacity = "0";
        node.style.transform = "scale(0.93)";
        node.style.transformOrigin = "top center";
        import("gsap").then(({ default: gsap }) => {
          gsap.to(node, {
            scale: 1,
            opacity: 1,
            duration: 0.7,
            ease: "power2.out",
            onComplete: () => {
              node.style.transform = "";
              node.style.transformOrigin = "";
              node.style.opacity = "";
            },
          });
        });
      } else {
        // First load - start hidden, Loader's GSAP timeline will animate
        node.style.opacity = "0";
        node.style.transform = "scale(0.93)";
      }
    }
  }, [registerHeroRef]);
  // Case studies now live on /work page - no overlay state needed here
  const [mounted, setMounted] = useState(false);
  const [inHeroSection, setInHeroSection] = useState(true); // Track if we're in hero section
  // Homepage uses gold theme - card themes are on /work page
  const [hoverTheme, setHoverTheme] = useState<ThemeColors | null>(null); // Hover override (contact buttons)
  const [testTheme, setTestTheme] = useState<ThemeColors | null>(null); // Dev theme tester override
  const [debugTheme, setDebugTheme] = useState(false); // Pink debug overlay mode
  const [showStyleGuide, setShowStyleGuide] = useState(false); // Mission Control styleguide
  // isContactOpen, isManifestoOpen defined above for inputMode computation
  const [themeVersion, setThemeVersion] = useState(0); // Increments on theme change to force child re-renders
  // HSL state for components that need props (cursor)
  const [activeHue, setActiveHue] = useState(0);
  const [activeSaturation, setActiveSaturation] = useState(0);
  const [activeLightness, setActiveLightness] = useState(100);
  // Debug display state (for bg/fg RGB values)
  const [debugBg, setDebugBg] = useState({ r: 3, g: 3, b: 3 });
  const [debugFg, setDebugFg] = useState({ r: 255, g: 255, b: 255 });

  // ============================================================================
  // CENTRALIZED THEME ANIMATION SYSTEM
  // All theme colors are lerped here and exposed via CSS variables.
  // Components should use CSS variables (var(--theme-base-rgb), etc.) - no per-component logic.
  // See THEME.md for documentation.
  // ============================================================================

  // Active values (current, being lerped)
  // Initialize to neutral values - will be snapped to correct values on first render
  const activeTheme = useRef({
    baseR: 128, baseG: 128, baseB: 128,       // --theme-base-rgb (blobs, gradients)
    brightR: 128, brightG: 128, brightB: 128, // --theme-bright-rgb (gradient end)
    fgR: 128, fgG: 128, fgB: 128,             // --theme-fg-rgb (UI elements, cube)
    hue: 0, saturation: 0, lightness: 50,     // HSL for legacy/cursor
    // Background/foreground for light/dark mode lerping
    bgR: 10, bgG: 10, bgB: 10,                 // --background-rgb
    modeFgR: 255, modeFgG: 255, modeFgB: 255, // --foreground-rgb
  });

  // Target values (where we're lerping to)
  const targetThemeRef = useRef({
    baseR: 128, baseG: 128, baseB: 128,
    brightR: 128, brightG: 128, brightB: 128,
    fgR: 128, fgG: 128, fgB: 128,
    hue: 0, saturation: 0, lightness: 50,
    baseHex: "#808080", brightHex: "#808080", fgHex: "#808080",
    // Background/foreground for light/dark mode lerping
    bgR: 10, bgG: 10, bgB: 10,
    modeFgR: 255, modeFgG: 255, modeFgB: 255,
  });

  // Animation loop state
  const animationRef = useRef<number | null>(null);
  const isAnimatingRef = useRef(false);

  // Snap instantly instead of lerping to save GPU
  const LERP_SPEED = 1;

  // Start or continue the animation loop
  const startThemeAnimation = useCallback(() => {
    // If already animating, the loop will pick up the new targets
    // But if somehow stuck, force restart
    if (isAnimatingRef.current && animationRef.current) return;

    // Cancel any stuck animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    isAnimatingRef.current = true;

    let lastStateUpdate = 0;
    const STATE_UPDATE_INTERVAL = 50; // Throttle React state updates

    const animate = (timestamp: number) => {
      const active = activeTheme.current;
      const target = targetThemeRef.current;
      let hasChanges = false;

      // Helper to lerp a value
      const lerp = (current: number, target: number): number => {
        const diff = target - current;
        if (Math.abs(diff) < 0.5) return target; // Snap when close
        return current + diff * LERP_SPEED;
      };

      // Helper to lerp hue (wraps around 360)
      const lerpHue = (current: number, target: number): number => {
        let diff = target - current;
        if (diff > 180) diff -= 360;
        if (diff < -180) diff += 360;
        if (Math.abs(diff) < 0.5) return target;
        let result = current + diff * LERP_SPEED;
        if (result < 0) result += 360;
        if (result >= 360) result -= 360;
        return result;
      };

      // Lerp all RGB values
      const newBaseR = lerp(active.baseR, target.baseR);
      const newBaseG = lerp(active.baseG, target.baseG);
      const newBaseB = lerp(active.baseB, target.baseB);
      const newBrightR = lerp(active.brightR, target.brightR);
      const newBrightG = lerp(active.brightG, target.brightG);
      const newBrightB = lerp(active.brightB, target.brightB);
      const newFgR = lerp(active.fgR, target.fgR);
      const newFgG = lerp(active.fgG, target.fgG);
      const newFgB = lerp(active.fgB, target.fgB);
      const newHue = lerpHue(active.hue, target.hue);
      const newSaturation = lerp(active.saturation, target.saturation);
      const newLightness = lerp(active.lightness, target.lightness);
      // Light/dark mode background/foreground
      const newBgR = lerp(active.bgR, target.bgR);
      const newBgG = lerp(active.bgG, target.bgG);
      const newBgB = lerp(active.bgB, target.bgB);
      const newModeFgR = lerp(active.modeFgR, target.modeFgR);
      const newModeFgG = lerp(active.modeFgG, target.modeFgG);
      const newModeFgB = lerp(active.modeFgB, target.modeFgB);

      // Check if anything changed
      if (
        newBaseR !== active.baseR || newBaseG !== active.baseG || newBaseB !== active.baseB ||
        newBrightR !== active.brightR || newBrightG !== active.brightG || newBrightB !== active.brightB ||
        newFgR !== active.fgR || newFgG !== active.fgG || newFgB !== active.fgB ||
        newHue !== active.hue || newSaturation !== active.saturation || newLightness !== active.lightness ||
        newBgR !== active.bgR || newBgG !== active.bgG || newBgB !== active.bgB ||
        newModeFgR !== active.modeFgR || newModeFgG !== active.modeFgG || newModeFgB !== active.modeFgB
      ) {
        hasChanges = true;

        // Update active values
        active.baseR = newBaseR; active.baseG = newBaseG; active.baseB = newBaseB;
        active.brightR = newBrightR; active.brightG = newBrightG; active.brightB = newBrightB;
        active.fgR = newFgR; active.fgG = newFgG; active.fgB = newFgB;
        active.hue = newHue; active.saturation = newSaturation; active.lightness = newLightness;
        active.bgR = newBgR; active.bgG = newBgG; active.bgB = newBgB;
        active.modeFgR = newModeFgR; active.modeFgG = newModeFgG; active.modeFgB = newModeFgB;

        // Update CSS variables (the source of truth for all themed elements)
        const root = document.documentElement.style;
        root.setProperty('--theme-base-rgb', `${Math.round(newBaseR)}, ${Math.round(newBaseG)}, ${Math.round(newBaseB)}`);
        root.setProperty('--theme-bright-rgb', `${Math.round(newBrightR)}, ${Math.round(newBrightG)}, ${Math.round(newBrightB)}`);
        root.setProperty('--theme-fg-rgb', `${Math.round(newFgR)}, ${Math.round(newFgG)}, ${Math.round(newFgB)}`);
        root.setProperty('--active-hue', String(newHue));
        root.setProperty('--active-saturation', `${newSaturation}%`);
        root.setProperty('--active-lightness', `${newLightness}%`);
        // Light/dark mode background/foreground
        root.setProperty('--background-rgb', `${Math.round(newBgR)}, ${Math.round(newBgG)}, ${Math.round(newBgB)}`);
        root.setProperty('--foreground-rgb', `${Math.round(newModeFgR)}, ${Math.round(newModeFgG)}, ${Math.round(newModeFgB)}`);
        root.setProperty('--background', `rgb(${Math.round(newBgR)}, ${Math.round(newBgG)}, ${Math.round(newBgB)})`);
        root.setProperty('--foreground', `rgb(${Math.round(newModeFgR)}, ${Math.round(newModeFgG)}, ${Math.round(newModeFgB)})`);

        // Hex values (for elements that need hex, computed from current RGB)
        const toHex = (r: number, g: number, b: number) =>
          '#' + [r, g, b].map(x => Math.round(x).toString(16).padStart(2, '0')).join('');
        root.setProperty('--theme-base', toHex(newBaseR, newBaseG, newBaseB));
        root.setProperty('--theme-bright', toHex(newBrightR, newBrightG, newBrightB));
        root.setProperty('--theme-fg', toHex(newFgR, newFgG, newFgB));
      }

      // Throttled React state update for components using props
      if (hasChanges && timestamp - lastStateUpdate > STATE_UPDATE_INTERVAL) {
        lastStateUpdate = timestamp;
        setActiveHue(active.hue);
        setActiveSaturation(active.saturation);
        setActiveLightness(active.lightness);
        // Update debug display state
        setDebugBg({ r: Math.round(active.bgR), g: Math.round(active.bgG), b: Math.round(active.bgB) });
        setDebugFg({ r: Math.round(active.modeFgR), g: Math.round(active.modeFgG), b: Math.round(active.modeFgB) });
      }

      // Continue or stop
      if (hasChanges) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        isAnimatingRef.current = false;
        animationRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Listen for open-contact event (from cube click in Hero)
  useEffect(() => {
    const handleOpenContact = () => navigateToDomain("contact");
    window.addEventListener("open-contact", handleOpenContact);
    return () => window.removeEventListener("open-contact", handleOpenContact);
  }, [navigateToDomain]);

  // Snap contact ring to TEC moon (index 1) when coming from Third Eye link
  useEffect(() => {
    const handleFocusTec = () => {
      const tecIndex = 1; // CONTACT_MOONS order: email=0, tec=1, linkedin=2
      contactSnapTargetRef.current = -tecIndex * CONTACT_ORB_SPACING;
      contactSpringVelRef.current = 0;
      startContactSpringAnimation();
    };
    window.addEventListener("focus-contact-tec", handleFocusTec);
    return () => window.removeEventListener("focus-contact-tec", handleFocusTec);
  }, [CONTACT_ORB_SPACING, startContactSpringAnimation]);

  // Page Visibility API - pause all animations when tab is hidden
  // Prevents lag/jank when returning to the tab after being backgrounded
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Access Lenis via window (set by useSmoothScroll hook)
      const lenis = (window as Window & { __lenis?: { stop: () => void; start: () => void } }).__lenis;

      if (document.hidden) {
        // Cancel theme animation
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;

        // Cancel spring animations
        if (springRafRef.current) {
          cancelAnimationFrame(springRafRef.current);
          springRafRef.current = null;
        }
        if (contactSpringRafRef.current) {
          cancelAnimationFrame(contactSpringRafRef.current);
          contactSpringRafRef.current = null;
        }

        // Cancel keyboard spin
        if (keySpinRafRef.current) {
          cancelAnimationFrame(keySpinRafRef.current);
          keySpinRafRef.current = null;
        }
        keySpinDirectionRef.current = null;

        // Cancel scroll animations
        if (scrollAnimationRef.current) {
          cancelAnimationFrame(scrollAnimationRef.current);
          scrollAnimationRef.current = null;
        }
        if (readScrollRafRef.current) {
          cancelAnimationFrame(readScrollRafRef.current);
          readScrollRafRef.current = null;
        }

        // Pause Lenis smooth scroll
        lenis?.stop();

        // Pause GSAP ticker (which drives Lenis)
        const gsap = (await import("gsap")).default;
        gsap.ticker.sleep();
      } else {
        // Resume GSAP ticker
        const gsap = (await import("gsap")).default;
        gsap.ticker.wake();

        // Resume Lenis
        lenis?.start();

        // Resume theme animation
        startThemeAnimation();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [startThemeAnimation]);

  // Track if this is the first theme update (snap instead of animate)
  const isFirstThemeUpdate = useRef(true);

  // Track previous target to avoid unnecessary themeVersion increments
  const prevTargetBaseRef = useRef<string | null>(null);

  // Compute project theme from active project's brandColor when viewing case study
  // This kicks in during peek/read mode (scrollProgress > 0)
  const isViewingCaseStudy = scrollProgress > 0 && activeDomain === "projects";
  const projectTheme: ThemeColors | null = useMemo(() => {
    if (!activeProject?.brandColor || !isViewingCaseStudy) return null;
    const brandHue = hexToHue(activeProject.brandColor);
    const brightColor = activeProject.brandColorEnd ?? activeProject.brandColor;
    return {
      base: activeProject.brandColor,
      bright: brightColor,
      hue: brandHue,
    };
  }, [activeProject, isViewingCaseStudy]);

  // Theme priority: debugTheme > testTheme (dev) > hoverTheme > projectTheme > idle (gold in manifesto/deck, white in hero/contact)
  const hasActiveTheme = debugTheme || testTheme !== null || hoverTheme !== null || projectTheme !== null;
  const isWhiteTheme = !hasActiveTheme && inHeroSection;

  // Light mode uses darker colors (28% lightness) for better contrast, dark mode uses brighter (50%)
  const coloredLightness = themeMode === "light" ? 28 : 50;
  let targetTheme: ThemeColors;
  let targetHue: number;
  let targetSaturation: number;
  let targetLightness: number;

  if (debugTheme) {
    // Pink debug mode - highest priority
    targetTheme = PINK_DEBUG_THEME;
    targetHue = PINK_DEBUG_THEME.hue;
    targetSaturation = 100;
    targetLightness = coloredLightness;
  } else if (testTheme !== null) {
    // Dev tester override
    targetTheme = testTheme;
    targetHue = testTheme.hue;
    targetSaturation = 100;
    targetLightness = coloredLightness;
  } else if (hoverTheme !== null) {
    // Contact button hover
    targetTheme = hoverTheme;
    targetHue = hoverTheme.hue;
    targetSaturation = 100;
    targetLightness = coloredLightness;
  } else if (projectTheme !== null) {
    // Case study view - use project's brandColor
    targetTheme = projectTheme;
    targetHue = projectTheme.hue;
    targetSaturation = 100;
    targetLightness = coloredLightness;
  } else if (inHeroSection) {
    // Idle in hero - white/neutral theme
    targetTheme = NONE_THEME;
    targetHue = 50; // Gold hue for smooth transitions
    targetSaturation = 0;
    targetLightness = themeMode === "light" ? 0 : 100;
  } else {
    // Idle elsewhere (manifesto, deck section) - gold theme
    targetTheme = GOLD_THEME;
    targetHue = GOLD_THEME.hue;
    targetSaturation = 100;
    targetLightness = coloredLightness;
  }

  // Handle hover on themed elements (cards with brand colors)
  // Debounce null to prevent flicker when moving between same-color cards (e.g., Panera)
  const hoverClearTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleHoverTheme = useCallback((theme: ThemeColors | null) => {
    // Clear any pending timeout
    if (hoverClearTimeoutRef.current) {
      clearTimeout(hoverClearTimeoutRef.current);
      hoverClearTimeoutRef.current = null;
    }

    if (theme === null) {
      // Delay clearing hover theme to allow moving between same-color cards
      hoverClearTimeoutRef.current = setTimeout(() => {
        setHoverTheme(null);
        hoverClearTimeoutRef.current = null;
      }, 100);
    } else {
      // Immediately set new hover theme
      setHoverTheme(theme);
    }
  }, []);

  // Update target theme values and trigger animation
  // This is the only place that sets theme targets - all lerping happens in the animation loop
  useEffect(() => {
    const isLight = themeMode === "light";
    const isNeutralTheme = targetTheme.base === NONE_THEME.base;
    const target = targetThemeRef.current;

    let baseR: number, baseG: number, baseB: number;
    let brightR: number, brightG: number, brightB: number;
    let baseHex: string, brightHex: string, fgHex: string;

    if (isNeutralTheme) {
      // Neutral theme: gradient text needs different base/bright for visible shimmer
      // Light mode: black base with gray shimmer
      // Dark mode: medium gray base with white shimmer
      if (isLight) {
        baseR = baseG = baseB = 0;        // black
        brightR = brightG = brightB = 100; // gray shimmer
        baseHex = "#000000";
        brightHex = "#646464";
        fgHex = "#000000";
      } else {
        baseR = baseG = baseB = 160;      // medium light gray
        brightR = brightG = brightB = 255; // white shimmer
        baseHex = "#a0a0a0";
        brightHex = "#ffffff";
        fgHex = "#ffffff";
      }
    } else {
      // Themed: apply lightness adjustments
      const adjustment = isLight ? THEME_ADJUSTMENT.light : THEME_ADJUSTMENT.dark;
      baseHex = adjustColorLightness(targetTheme.base, adjustment);
      brightHex = adjustColorLightness(targetTheme.bright, adjustment);
      fgHex = baseHex;

      // Parse to RGB
      const hexMatch = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(baseHex);
      if (hexMatch) {
        baseR = parseInt(hexMatch[1], 16);
        baseG = parseInt(hexMatch[2], 16);
        baseB = parseInt(hexMatch[3], 16);
      } else {
        baseR = baseG = baseB = isLight ? 0 : 255;
      }

      const brightMatch = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(brightHex);
      if (brightMatch) {
        brightR = parseInt(brightMatch[1], 16);
        brightG = parseInt(brightMatch[2], 16);
        brightB = parseInt(brightMatch[3], 16);
      } else {
        brightR = brightG = brightB = isLight ? 0 : 255;
      }
    }

    // Foreground RGB (for UI elements - cube, borders, text)
    let fgR: number, fgG: number, fgB: number;
    if (isNeutralTheme) {
      const fg = isLight ? 0 : 255;
      fgR = fgG = fgB = fg;
    } else {
      fgR = baseR;
      fgG = baseG;
      fgB = baseB;
    }

    // Set TARGET values (animation loop will lerp active toward these)
    target.baseR = baseR; target.baseG = baseG; target.baseB = baseB;
    target.brightR = brightR; target.brightG = brightG; target.brightB = brightB;
    target.fgR = fgR; target.fgG = fgG; target.fgB = fgB;
    target.hue = targetHue;
    target.saturation = targetSaturation;
    target.lightness = targetLightness;
    target.baseHex = baseHex;
    target.brightHex = brightHex;
    target.fgHex = fgHex;
    // Light/dark mode background/foreground targets
    // Dark: bg=#030303 (3,3,3), fg=#ffffff (255,255,255)
    // Light: bg=#f3f3f3 (243,243,243), fg=#0a0a0a (10,10,10)
    target.bgR = isLight ? 243 : 10;
    target.bgG = isLight ? 243 : 10;
    target.bgB = isLight ? 243 : 10;
    target.modeFgR = isLight ? 10 : 255;
    target.modeFgG = isLight ? 10 : 255;
    target.modeFgB = isLight ? 10 : 255;

    // Helper to snap theme values instantly (no animation)
    const snapToTarget = () => {
      const active = activeTheme.current;
      active.baseR = baseR; active.baseG = baseG; active.baseB = baseB;
      active.brightR = brightR; active.brightG = brightG; active.brightB = brightB;
      active.fgR = fgR; active.fgG = fgG; active.fgB = fgB;
      active.hue = targetHue; active.saturation = targetSaturation; active.lightness = targetLightness;
      active.bgR = target.bgR; active.bgG = target.bgG; active.bgB = target.bgB;
      active.modeFgR = target.modeFgR; active.modeFgG = target.modeFgG; active.modeFgB = target.modeFgB;

      const root = document.documentElement.style;
      root.setProperty('--theme-base-rgb', `${Math.round(baseR)}, ${Math.round(baseG)}, ${Math.round(baseB)}`);
      root.setProperty('--theme-bright-rgb', `${Math.round(brightR)}, ${Math.round(brightG)}, ${Math.round(brightB)}`);
      root.setProperty('--theme-fg-rgb', `${Math.round(fgR)}, ${Math.round(fgG)}, ${Math.round(fgB)}`);
      root.setProperty('--theme-base', baseHex);
      root.setProperty('--theme-bright', brightHex);
      root.setProperty('--theme-fg', fgHex);
      root.setProperty('--active-hue', String(targetHue));
      root.setProperty('--active-saturation', `${targetSaturation}%`);
      root.setProperty('--active-lightness', `${targetLightness}%`);
      root.setProperty('--background-rgb', `${target.bgR}, ${target.bgG}, ${target.bgB}`);
      root.setProperty('--foreground-rgb', `${target.modeFgR}, ${target.modeFgG}, ${target.modeFgB}`);
      root.setProperty('--background', `rgb(${target.bgR}, ${target.bgG}, ${target.bgB})`);
      root.setProperty('--foreground', `rgb(${target.modeFgR}, ${target.modeFgG}, ${target.modeFgB})`);

      setActiveHue(targetHue);
      setActiveSaturation(targetSaturation);
      setActiveLightness(targetLightness);
      setDebugBg({ r: target.bgR, g: target.bgG, b: target.bgB });
      setDebugFg({ r: target.modeFgR, g: target.modeFgG, b: target.modeFgB });
    };

    // On first run, snap active to target (no animation on initial load)
    if (isFirstThemeUpdate.current) {
      isFirstThemeUpdate.current = false;
      snapToTarget();
      return;
    }

    // If animations disabled (reduced motion or low GPU), snap instead of animate
    if (!canAnimate) {
      snapToTarget();
      return;
    }

    // Start animation to lerp from current to target
    startThemeAnimation();

    // Only increment version if target actually changed (avoids unnecessary child re-renders)
    // Use baseHex as identifier since it captures the primary color change
    if (prevTargetBaseRef.current !== baseHex) {
      prevTargetBaseRef.current = baseHex;
      setThemeVersion(v => v + 1);
    }
  }, [targetHue, targetSaturation, targetLightness, targetTheme, themeMode, canAnimate, startThemeAnimation]);

  // Case studies now live on /work page - no case study theme effects needed

  // Initialize smooth scroll
  const lenisRef = useSmoothScroll();

  // Refs to track current values and avoid unnecessary state updates
  const currentInHeroRef = useRef(true);
  const styleGuideOpenRef = useRef(false);

  useEffect(() => {
    styleGuideOpenRef.current = showStyleGuide;
  }, [showStyleGuide]);

  // Track hero section based on scroll position
  const COMMIT_THRESHOLD = 0.4;

  useEffect(() => {
    const handleScroll = () => {
      // Skip scroll tracking when style guide is open
      if (styleGuideOpenRef.current) return;

      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;

      // Hero exits when we've scrolled past the commit threshold
      const newInHero = scrollY < viewportHeight * COMMIT_THRESHOLD;
      if (newInHero !== currentInHeroRef.current) {
        currentInHeroRef.current = newInHero;
        setInHeroSection(newInHero);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Set initial state
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMounted(true);
    // Prevent browser from restoring scroll position on refresh
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
    // Force scroll to top on page load
    window.scrollTo(0, 0);
  }, []);


  // Case study navigation now handled by /work page

  // Spacebar cycles through color themes (dev only)
  useEffect(() => {
    if (!IS_DEV) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      // Don't interfere with form inputs
      const tagName = (e.target as HTMLElement).tagName;
      if (["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(tagName)) return;

      e.preventDefault(); // Prevent default scroll

      // Find current theme index
      const currentIndex = colorThemes.findIndex(
        (t) => t.theme?.base === testTheme?.base
      );
      // Cycle to next theme
      const nextIndex = (currentIndex + 1) % colorThemes.length;
      setTestTheme(colorThemes[nextIndex].theme);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [testTheme]);




  // Project interaction handlers moved to /work page

  // ==========================================================================
  // ORBIT A11Y: DOM focus layer + peek / reader dialogs
  // ==========================================================================
  // The front node's button in OrbitFocusLayer. Dialogs opened from the orbit
  // hand focus back to it on close. The roving stop always tracks the front
  // node, so this is the node the viewer is on even after rotating in peek or
  // paging projects inside the reader.
  const activeNodeRef = useRef<HTMLButtonElement | null>(null);
  const orbitLayerActive = boot.isComplete && activeDomain === "projects";
  const lockedSlugs = useMemo(
    () => new Set(publicProjects.filter(p => p.isProtected && !unlockedProjects.has(p.id)).map(p => p.id)),
    [unlockedProjects]
  );

  // Peek card is a dialog once its controls are revealed (OrbCard shows them past 0.5).
  const peekDialogOpen = activeDomain === "projects" && zone.isPeek && peekProgress >= 0.5;

  // Reader: `readerRendered` is the exact render condition of the reader JSX below.
  // It counts as open (trap focus, Escape closes) from the same point the URL
  // switches to the slug and focus moves into it (see the URL SYNC effect).
  const readerRendered =
    (boot.isComplete || deepLinkFadeProgress > 0) &&
    !!activeProject &&
    (scrollProgress > 0 || deepLinkFadeProgress > 0) &&
    activeDomain === "projects" &&
    !(activeProjectIsProtected && !unlockedProjects.has(activeProject.id));
  const readerOpen = readerRendered && zone.isRead && (scrollProgress >= 0.8 || deepLinkFadeProgress >= 1);
  useFocusTrap(readScrollContainerRef, readerOpen, activeNodeRef);

  // Escape closes the reader. The main key handler bails while any dialog is
  // open, so the reader needs its own. Lightbox / palette / overview / password
  // dialogs opened on top of it own their Escape and are left alone.
  const isLightboxOpenRef = useRef(isLightboxOpen);
  isLightboxOpenRef.current = isLightboxOpen;
  useEffect(() => {
    if (!readerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || isLightboxOpenRef.current) return;
      const reader = readScrollContainerRef.current;
      const otherDialog = Array.from(document.querySelectorAll('[role="dialog"]')).some(
        d => d !== reader && !d.hasAttribute("data-orbit-peek")
      );
      if (otherDialog) return;
      e.preventDefault();
      handleClosePeek();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [readerOpen, handleClosePeek]);

  if (!mounted) {
    return null;
  }

  return (
      <div className="relative min-h-dvh bg-[var(--background)]">
      {/* Skip link - FIRST tab stop on the page (GUIDELINES §Skip Link). Moves
          focus into <main> without touching the URL hash (pushState routing). */}
      <a
        href="#main-content"
        className="skip-link sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[10001] focus:px-4 focus:py-2 focus:text-[var(--background)] focus:rounded-lg focus:text-sm focus:font-medium"
        onClick={(e) => {
          const target = document.getElementById("main-content");
          if (!target) return;
          e.preventDefault();
          target.focus();
        }}
      >
        Skip to main content
      </a>

      {/* Star Loader - Phase 0 of boot sequence */}
      {boot.phase === "loader" && (
        <StarLoader
          assetsReady={boot.assetsLoaded}
          onComplete={() => {
            // StarLoader calls this when bloop animation completes
            // useBootSequence handles the state transition internally via RAF
          }}
        />
      )}

      {/* CRT Boot Text - visible during loader/void, fades when ring forms */}
      <CRTBootText
        visible={boot.phase === "loader" || boot.phase === "void"}
        fadeOut={boot.phase === "ring" && boot.phaseProgress < 0.3}
        crtEnabled={crtEnabled}
        version={process.env.NEXT_PUBLIC_APP_VERSION}
      />

      {/* Boot Starfield - Phases 1-2 (void, ring with emergence) */}
      {/* Stays visible after boot completes - this IS the interactive ring */}
      {/*
        Meta ring visibility logic:
        - Show project ring only when at main AND not transitioning away from main
        - Show meta ring when at non-main OR when transitioning away from main
        This ensures users SEE the spin animation when navigating to Contact/Manifesto
      */}
      <BootStarfield
        phase={boot.phase}
        phaseProgress={boot.phaseProgress}
        isComplete={boot.isComplete}
        // Pause once the reader is committed: full read, backgrounded, > halfway
        // through the peek→read scroll morph, any full-screen modal, blog deep scroll, or content domains.
        // Un-pause when user reaches bottom (engaged visitor reward).
        paused={zone.isRead || scrollProgress > 0.5 || backgrounded || overviewOpen || paletteOpen || !!passwordPromptProject || blogDeepScroll || blogLightboxOpen || (resumeScrolled && !resumeAtBottom) || (manifestoScrolled && !manifestoAtBottom)}
        ringAngle={ringAngle}
        contactRingAngle={contactRingAngle}
        activeIndex={activeIndex}
        activeContactIndex={activeContactIndex}
        isPeeking={isPeeking}
        morphProgress={scrollProgress}
        isBackgroundMode={isRingBackgroundMode}
        hideRing={isRingBackgroundMode}
        showProjectRing={
          // Project ring shows ONLY when at main with no transition away from main
          // Otherwise show meta ring (so user sees the spin animation)
          activeDomain === "projects" && (targetDomain === null || targetDomain === "projects")
        }
        activeDomainId={targetDomain ?? activeDomain}
        metaAngle={metaRing.metaAngle}
        z1Height={zone.z1Height}
        z2Height={zone.z2Height}
        onOrbFocus={handleOrbFocus}
        onOrbFocusFromBackground={handleOrbFocusFromBackground}
        onMetaOrbClick={navigateToDomain}
        onBackgroundClick={undefined} // Peek only closes via X button
        onHoveredSideChange={setOrbHoveredSide}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onContactDragStart={handleContactDragStart}
        onContactDragMove={handleContactDragMove}
        onContactDragEnd={handleContactDragEnd}
        onContactRotateToMoon={handleContactRotateToMoon}
        onSpinChange={setIsRingSpinning}
        onGeometryChange={setRingGeometry}
        isLightMode={themeMode === "light"}
        onStripHoverChange={setStripHoveredIndex}
        deepLinkFadeProgress={deepLinkFadeProgress}
        gpuTier={gpuTier}
      />

      {/* Main content - the orbit's DOM layer. Sits right after the canvas so the
          tab order runs skip link → focused orbit node → card arrows → settings
          → corner nav. No z-index: a stacking context here would trap the focus
          ring (fixed, z-6) under the canvas (z-5). */}
      <main
        id="main-content"
        ref={heroRefCallback}
        tabIndex={-1}
        style={{
          position: "relative",
          transformOrigin: "top center",
          // Programmatic focus target for the skip link, not a control.
          outline: "none",
        }}
      >
        {/* Boot sequence is full-screen, no scroll content needed */}
        <div className="h-dvh" aria-hidden="true" />
        {orbitLayerActive && (
          <OrbitFocusLayer
            orbs={PROJECT_ORBS}
            activeIndex={activeIndex}
            onActivate={handleOrbFocus}
            geometry={ringGeometry}
            isIdle={zone.isIdle && scrollProgress === 0}
            activeNodeRef={activeNodeRef}
            lockedSlugs={lockedSlugs}
          />
        )}
      </main>

      {/* Content dim overlay - dims canvas for blog/resume content domains */}
      <div
        className="fixed inset-0 z-[5] pointer-events-none"
        style={{
          backgroundColor: themeMode === "light" ? "rgba(240,240,240,0.6)" : "rgba(0,0,0,0.6)",
          opacity: (blogDeepScroll && activeDomain === "blog") || activeDomain === "resume" ? 1 : 0,
          transition: "opacity 400ms ease-out",
        }}
      />

      {/* Strip Lock Icon - show lock on VPR when in strip mode */}
      {/* Hidden when lightbox is open */}
      {boot.isComplete && scrollProgress > 0.3 && activeDomain === "projects" && !unlockedProjects.has("vpr") && !isLightboxOpen && ringGeometry && (() => {
        // Use ringGeometry dimensions to match canvas exactly
        const windowWidth = ringGeometry.viewportWidth;
        const windowHeight = ringGeometry.viewportHeight;
        const visibleHeight3D = 2 * 10 * Math.tan((60 / 2) * Math.PI / 180);
        const unitsToPixels = windowHeight / visibleHeight3D;
        const visibleWidth3D = visibleHeight3D * (windowWidth / windowHeight);

        const orbCount = 6;
        const safeWidth3D = visibleWidth3D * 0.6;
        const maxSpacing3D = Math.min(0.45, visibleWidth3D * 0.065);
        const stripSpacing3D = Math.min(maxSpacing3D, safeWidth3D / (orbCount - 1));
        const stripSpacing = stripSpacing3D * unitsToPixels;

        // VPR is index 0
        const vprIndex = 0;
        const offsetFromCenter = (vprIndex - (orbCount - 1) / 2) * stripSpacing;
        const vprX = windowWidth / 2 + offsetFromCenter - 4; // -4px nudge to center on orb
        const stripY = zone.z1Height * 0.5;

        return (
          <div
            className="fixed z-[150] pointer-events-none"
            style={{
              left: vprX,
              top: stripY,
              transform: 'translate(-50%, -50%)',
              opacity: Math.min(1, scrollProgress * 2),
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))',
              }}
            >
              <path
                d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z"
                fill="rgba(var(--foreground-rgb),0.95)"
              />
              <path
                d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11"
                stroke="rgba(var(--foreground-rgb),0.95)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        );
      })()}

      {/* Unified Orb Card - morphs from idle preview to peek detail */}
      {/* Only shows when Projects domain is active (not during Manifesto/Contact) */}
      {/* Fades out as user scrolls from peek to read mode */}
      {boot.isComplete && ringGeometry && activeDomain === "projects" && (
        <div
          style={{
            opacity: 1 - scrollProgress,
            pointerEvents: scrollProgress > 0.5 ? 'none' : 'auto',
            transition: 'opacity 0.1s ease-out',
          }}
        >
          <OrbCard
            geometry={ringGeometry}
            activeOrb={activeIndex === -1 ? PROFILE_ORB : PROJECT_ORBS[activeIndex]}
            isProfile={activeIndex === -1}
            isPeeking={isPeeking}
            peekProgress={peekProgress}
            z3Height={morphedZ3Height}
            scrollProgress={scrollProgress}
            onPeekToggle={() => {
              // Only open peek - closing is handled by X button (onClose)
              if (!isPeeking) {
                zone.goToPeek(activeIndex);
                window.history.pushState({}, "", DOMAIN_TO_PATH.projects);
              }
            }}
            onClose={handleClosePeek}
            onRotateLeft={handleRotateLeft}
            onRotateRight={handleRotateRight}
            orbHoveredSide={orbHoveredSide}
            arrowKeyPressed={arrowKeyPressed}
            isProtected={activeProjectIsProtected}
            isUnlocked={activeOrbSlug ? unlockedProjects.has(activeOrbSlug) : false}
            project={activeProject}
            onPasswordSuccess={handlePasswordSuccess}
            crtEnabled={crtEnabled}
            isLightMode={themeMode === "light"}
            dialogOpen={peekDialogOpen}
            returnFocusRef={activeNodeRef}
            onReadRequest={enterReadFromPeek}
          />
        </div>
      )}

      {/* Contact Card - shows when viewing contact system */}
      {boot.isComplete && activeDomain === "contact" && (
        <ContactCard
          geometry={ringGeometry}
          activeIndex={activeContactIndex}
          z3Height={zone.z3Height}
          onRotateLeft={handleContactRotateLeft}
          onRotateRight={handleContactRotateRight}
          arrowKeyPressed={contactArrowKeyPressed}
          isLightMode={themeMode === "light"}
        />
      )}

      {/* Read Mode Scroll Container - Case Study content with close button */}
      {/* Renders during peek-to-read transition with interpolated opacity/position */}
      {/* Also shows during deep link cross-fade (even before boot.isComplete) */}
      {/* BLOCKED for protected projects until unlocked */}
      {readerRendered && activeProject && (() => {
        // Merge protected content with base project if unlocked
        const protectedContent = protectedContentMap.get(activeProject.id);
        const effectiveProject = protectedContent
          ? { ...activeProject, ...protectedContent }
          : activeProject;
        const brandColor = effectiveProject.brandColor ?? "#FF5E00";

        // Interpolate opacity and position based on scrollProgress
        // scrollProgress: 0 = just entered peek, 1 = fully in read mode
        // For deep links: cross-fade with particles (read fades in as particles fade out)
        const normalReadOpacity = Math.min(1, scrollProgress * 1.5); // Fade in faster
        const readOpacity = deepLinkFadeProgress > 0
          ? Math.max(normalReadOpacity, deepLinkFadeProgress)
          : normalReadOpacity;
        const readTranslateY = deepLinkFadeProgress > 0 ? 0 : (1 - scrollProgress) * 100; // Skip slide animation for deep links
        const isFullyInRead = scrollProgress >= 0.95 || deepLinkFadeProgress >= 1;

        return (
          <>
          <div
            ref={readScrollContainerRef}
            role="dialog"
            aria-modal="true"
            aria-label={`${effectiveProject.title} case study`}
            data-orbit-reader
            tabIndex={-1}
            className="fixed inset-x-0 z-[100] scroll-instant"
            onWheel={(e) => {
              // Only stop propagation when fully in read mode
              // During transition, let events reach peek-to-read handler
              if (isFullyInRead) {
                e.stopPropagation();
              }
            }}
            onScroll={(e) => {
              // Track if scrolled to bottom (for gradient fade)
              const el = e.currentTarget;
              const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
              setReadIsAtBottom(atBottom);
            }}
            style={{
              top: 52, // Below nav strip (Z1_HEIGHTS.read)
              bottom: 0,
              // Programmatic-focus container (tabIndex -1), not a control — the global
              // :focus-visible rule would otherwise ring its whole edge in the theme color
              // (a full-width "glow" line under the nav strip). Children keep their own rings.
              // Inline: the global rule is unlayered and beats a Tailwind outline-none class.
              outline: 'none',
              overflowY: isFullyInRead ? 'scroll' : 'hidden', // Force scrollbar when in read
              WebkitOverflowScrolling: 'touch', // Momentum scroll on iOS
              scrollbarWidth: 'thin',
              scrollbarColor: `${brandColor}40 transparent`,
              opacity: readOpacity,
              transform: isFullyInRead ? 'none' : `translateY(${readTranslateY}px)`, // Remove transform when in read
              pointerEvents: (scrollProgress > 0.5 || deepLinkFadeProgress > 0.5) ? 'auto' : 'none',
              // Background with brand color glow
              background: `
                radial-gradient(ellipse 80% 50% at 50% 0%, ${brandColor}25 0%, transparent 50%),
                linear-gradient(to bottom, rgb(var(--background-rgb)) 0%, rgb(var(--background-rgb)) 100%)
              `,
            }}
          >
            {/* Close button - sticky at top with gradient for contrast */}
            <div
              className="sticky top-0 z-10 flex justify-center pt-4 pb-12"
              style={{
                background: `radial-gradient(ellipse 80% 100% at 50% 0%, rgba(${getScrimColor(themeMode === "light")},0.6) 0%, rgba(${getScrimColor(themeMode === "light")},0.3) 50%, transparent 80%)`,
              }}
            >
              <Button
                variant="icon-sm"
                magnetic={false}
                className="text-[var(--toggle-icon)] !bg-transparent !backdrop-blur-none hover:!bg-[rgba(var(--surface-rgb),0.6)] hover:!backdrop-blur-xl transition-all"
                onClick={handleClosePeek}
                aria-label="Close"
              >
                <CloseIcon size={ICON_SIZE_SM} />
              </Button>
            </div>

            {/* Case Study Content */}
            <CaseStudyInline
              project={effectiveProject}
              scrollProgress={scrollProgress}
              onClose={handleClosePeek}
              onNavigateToProject={handleNavigateToProject}
              onLightboxChange={setIsLightboxOpen}
              crtEnabled={crtEnabled}
              orbIndex={activeIndex + 1}
            />
          </div>

          {/* Bottom gradient - hides when scrolled to end */}
          <div
            className="fixed left-0 right-0 z-[101] pointer-events-none"
            style={{
              bottom: 0,
              height: 200,
              background: `linear-gradient(to top, rgba(${getScrimColor(themeMode === "light")},0.5) 0%, rgba(${getScrimColor(themeMode === "light")},0.2) 40%, transparent 100%)`,
              opacity: readIsAtBottom ? 0 : scrollProgress,
              transition: "opacity 300ms ease-out",
            }}
          />

          {/* Strip click zone - captures clicks on strip orbs while in read mode */}
          <div
            className="fixed inset-x-0 top-0"
            style={{ height: 52, cursor: 'pointer', zIndex: 100, pointerEvents: 'auto' }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              // Calculate which orb was clicked based on x position
              const windowWidth = window.innerWidth;
              const windowHeight = window.innerHeight;
              const visibleHeight3D = 2 * 10 * Math.tan((60 / 2) * Math.PI / 180);
              const unitsToPixels = windowHeight / visibleHeight3D;
              const visibleWidth3D = visibleHeight3D * (windowWidth / windowHeight);

              const orbCount = 6;
              const safeWidth3D = visibleWidth3D * 0.6;
              const maxSpacing3D = Math.min(0.45, visibleWidth3D * 0.065);
              const stripSpacing3D = Math.min(maxSpacing3D, safeWidth3D / (orbCount - 1));
              const stripSpacing = stripSpacing3D * unitsToPixels;

              const clickX = e.clientX;
              let closestIndex = 0;
              let closestDist = Infinity;

              for (let i = 0; i < orbCount; i++) {
                const offsetFromCenter = (i - (orbCount - 1) / 2) * stripSpacing;
                const orbX = windowWidth / 2 + offsetFromCenter;
                const dist = Math.abs(clickX - orbX);
                if (dist < closestDist) {
                  closestDist = dist;
                  closestIndex = i;
                }
              }

              // Only trigger if click is reasonably close to an orb
              if (closestDist < 60) {
                handleOrbFocus(closestIndex);
              }
            }}
          />
          </>
        );
      })()}

      {/* Manifesto Overlay - floating text in Z2 when manifesto active */}
      {boot.isComplete && (
        <ManifestoOverlay
          isActive={activeDomain === "manifesto"}
          z1Height={zone.z1Height}
          z2Height={zone.z2Height}
          crtEnabled={crtEnabled}
          isLightMode={themeMode === "light"}
          onScrolledChange={handleManifestoScrolled}
          onAtBottomChange={handleManifestoAtBottom}
        />
      )}

      {/* Resume Overlay - resume card in Z2 when resume active */}
      {boot.isComplete && (
        <ResumeOverlay
          isActive={activeDomain === "resume"}
          z1Height={zone.z1Height}
          z2Height={zone.z2Height}
          crtEnabled={crtEnabled}
          isLightMode={themeMode === "light"}
          onScrolledChange={handleResumeScrolled}
          onAtBottomChange={handleResumeAtBottom}
        />
      )}

      {/* Blog Overlay - single orb + scroll-to-read when blog active */}
      {boot.isComplete && (
        <BlogOverlay
          isActive={activeDomain === "blog"}
          z1Height={zone.z1Height}
          z2Height={zone.z2Height}
          crtEnabled={crtEnabled}
          isLightMode={themeMode === "light"}
          onDeepScrollChange={handleBlogDeepScroll}
          onScrolledChange={handleBlogScrolled}
          onLightboxChange={handleBlogLightboxChange}
          initialPostId={blogInitialPostId}
        />
      )}

      {/* Boot Identity Layer - shows during ring phase, adapts to zone state */}
      <BootIdentityLayer
          phase={boot.phase}
          phaseProgress={boot.phaseProgress}
          isComplete={boot.isComplete}
          zoneState={zone.zoneState}
          z1Height={zone.z1Height}
          isTinyViewport={zone.isTinyViewport}
          isCrampedViewport={zone.isCrampedViewport}
          isNarrowViewport={zone.isNarrowViewport}
          activeDomain={activeDomain}
          crtEnabled={crtEnabled}
          hideSubtitles={(blogScrolled && activeDomain === "blog") || (resumeScrolled && activeDomain === "resume") || (manifestoScrolled && activeDomain === "manifesto")}
          dimHero={blogScrolled && activeDomain === "blog"}
          isPeeking={isPeeking}
          peekProgress={peekProgress}
        />

      {/* Ambient gradient background */}
      <AmbientGradients
        className="z-0"
        paused={zone.isRead || scrollProgress > 0.5 || backgrounded || blogDeepScroll || blogLightboxOpen || (resumeScrolled && !resumeAtBottom) || (manifestoScrolled && !manifestoAtBottom) || overviewOpen || paletteOpen}
        isLightMode={themeMode === "light"}
        isNeutral={isWhiteTheme}
        isLoading={!hasLoaderBeenShown}
      />


      {/* Settings tray - collapsed gear icon, fans out toggles on click */}
      <ToggleTray crtEnabled={crtEnabled} onCrtToggle={() => setCrtEnabled(!crtEnabled)} />

      {/* Custom cursor - always white/black, doesn't follow theme */}
      <CustomCursor hue={activeHue} isWhiteTheme={true} isLightMode={themeMode === "light"} onHoverTheme={handleHoverTheme} />

      {/* NOTE: SpatialOSCanvas removed - boot sequence orbs ARE the final ring */}
      {/* If we need scroll-driven interactivity later, add it to BootStarfield */}


      {/* Overscroll extensions - cover rubber-band bounce without affecting scroll */}
      <div className="fixed top-0 left-0 right-0 h-dvh -translate-y-full bg-[var(--background)] pointer-events-none -z-10" aria-hidden="true" />
      <div className="fixed bottom-0 left-0 right-0 h-dvh translate-y-full bg-[var(--surface-bg)] pointer-events-none -z-10" aria-hidden="true" />

      {/* Case studies now live on /work page */}

      {/* Mission Control - Design System Styleguide */}
      <StyleGuide
        isOpen={showStyleGuide}
        onClose={() => setShowStyleGuide(false)}
        isLightMode={themeMode === "light"}
        overrideTheme={debugTheme ? PINK_DEBUG_THEME : testTheme}
      />

      {/* Strip tooltip - project preview on hover */}
      {scrollProgress > 0.5 && stripHoveredIndex !== null && ringGeometry && (() => {
        const windowWidth = ringGeometry.viewportWidth;
        const windowHeight = ringGeometry.viewportHeight;
        const visibleHeight3D = 2 * 10 * Math.tan((60 / 2) * Math.PI / 180);
        const unitsToPixels = windowHeight / visibleHeight3D;
        const visibleWidth3D = visibleHeight3D * (windowWidth / windowHeight);
        const orbCount = PROJECT_ORBS.length;
        const safeWidth3D = visibleWidth3D * 0.6;
        const maxSpacing3D = Math.min(0.45, visibleWidth3D * 0.065);
        const stripSpacing3D = Math.min(maxSpacing3D, safeWidth3D / (orbCount - 1));
        const stripSpacing = stripSpacing3D * unitsToPixels;
        const offsetFromCenter = (stripHoveredIndex - (orbCount - 1) / 2) * stripSpacing;
        const posX = windowWidth / 2 + offsetFromCenter;
        const posY = zone.z1Height * 0.5;
        const hoveredOrb = PROJECT_ORBS[stripHoveredIndex];
        const headline = hoveredOrb.headline.replace(/\n/g, ' ');

        return (
          <div
            key="strip-tooltip"
            className="fixed z-[500] pointer-events-none"
            style={{
              left: posX,
              top: posY + 32,
              transform: 'translateX(-50%)',
            }}
          >
            <div
              className="glass no-hover px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap"
              style={{
                background: 'rgba(30, 30, 30, 0.85)',
                color: 'rgba(255, 255, 255, 0.9)',
              }}
            >
              {headline}
            </div>
          </div>
        );
      })()}

      {/* Meta Ring Navigation System Views - DISABLED while debugging spin */}
      {/* TODO: Re-enable once meta ring spin is working
      <SystemView
        domainId="contact"
        activeDomain={activeDomain}
        previousDomain={previousDomain}
        transitionState={transitionState}
      >
        <ContactSystem onNavigate={navigateToDomain} />
      </SystemView>

      <SystemView
        domainId="manifesto"
        activeDomain={activeDomain}
        previousDomain={previousDomain}
        transitionState={transitionState}
      >
        <ManifestoSystem onNavigate={navigateToDomain} crtEnabled={crtEnabled} />
      </SystemView>
      */}

      {/* Zone Debug Overlays - visualize Z1/Z2/Z3 boundaries */}
      {process.env.NODE_ENV === "development" && (
        <>
          {/* Z1 - Hero Zone (top) */}
          <div
            className="fixed top-0 left-0 right-0 pointer-events-none z-[100]"
            style={{
              height: zone.z1Height,
              background: "rgba(255, 100, 100, 0.04)",
              borderBottom: "2px dashed rgba(255, 100, 100, 0.1)",
            }}
          >
            <span className="absolute bottom-1 left-2 text-[10px] font-mono text-red-400/70">
              Z1 · {zone.z1Height}px · {zone.isNarrowViewport ? "narrow" : zone.isCrampedViewport ? "cramped" : "normal"} · {viewportWidth}×{viewportHeight}
            </span>
          </div>

          {/* Z2 - Ring Zone (middle) */}
          <div
            className="fixed left-0 right-0 pointer-events-none z-[100]"
            style={{
              top: zone.z1Height,
              height: zone.z2Height,
              background: "rgba(100, 255, 100, 0.04)",
              borderBottom: "2px dashed rgba(100, 255, 100, 0.1)",
            }}
          >
            <span className="absolute top-1 left-2 text-[10px] font-mono text-green-400/70">
              Z2 · {zone.z2Height}px · ring
            </span>
          </div>

          {/* Z3 - Peek Zone (bottom) */}
          <div
            className="fixed left-0 right-0 bottom-0 pointer-events-none z-[100]"
            style={{
              height: zone.z3Height,
              background: "rgba(100, 100, 255, 0.04)",
              borderTop: "2px dashed rgba(100, 100, 255, 0.1)",
            }}
          >
            <span className="absolute top-1 left-2 text-[10px] font-mono text-blue-400/70">
              Z3 · {zone.z3Height}px · {zone.zoneState}
            </span>
          </div>
        </>
      )}

      {/* CRT Overlay - degraded space transmission aesthetic */}
      {crtEnabled && <CRTOverlay isSpinning={isRingSpinning} />}

      {/* Dynamic Corner Nav - Shows adjacent domains in circular meta ring order */}
      {/* Hidden when lightbox is open */}
      {boot.isComplete && !isLightboxOpen && (() => {
        const adjacent = getAdjacentDomains(activeDomain);
        return (
          <CornerNav
            activeDomain={activeDomain}
            leftDomain={adjacent.left}
            rightDomain={adjacent.right}
            isSpinning={metaRing.isSpinning || transitionState !== "idle"}
            spinTransition={metaRing.spinTransition}
            onSpinTo={navigateToDomain}
            getDomainLabel={metaRing.getDomainLabel}
            visibility={1}
            crtEnabled={crtEnabled}
          />
        );
      })()}

      {/* Orbital Overview trigger (top-center dot row) */}
      {boot.isComplete && !isLightboxOpen && (
        <OverviewTrigger
          onClick={() => { setOverviewOpen(true); setPaletteOpen(false); }}
          activeDomain={activeDomain}
          crtEnabled={crtEnabled}
        />
      )}

      {/* Orbital Overview — full-screen domain map */}
      {boot.isComplete && (
        <OrbitalOverview
          isOpen={overviewOpen}
          onClose={() => setOverviewOpen(false)}
          activeDomain={activeDomain}
          onNavigate={(d) => { setOverviewOpen(false); navigateToDomain(d); }}
          crtEnabled={crtEnabled}
          isLightMode={themeMode === "light"}
        />
      )}

      {/* Command Palette — Cmd+K searchable nav + quick actions */}
      {boot.isComplete && (
        <CommandPalette
          isOpen={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          activeDomain={activeDomain}
          onNavigate={(d) => { setPaletteOpen(false); navigateToDomain(d); }}
          crtEnabled={crtEnabled}
          onToggleCRT={() => setCrtEnabled(!crtEnabled)}
        />
      )}

      {/* Password Modal - shown when trying to access protected projects */}
      {passwordPromptProject && (
        <PasswordModal
          project={passwordPromptProject}
          onSuccess={handlePasswordSuccess}
          onClose={handlePasswordClose}
        />
      )}
    </div>
  );
}
